// =====================================================================
// PUBLIC CAREERS PORTAL - With Candidate Registration & Login System
// 3-Step Application Wizard + My Applications Dashboard
// =====================================================================
// Developed & Architected by Sachin Singh  E-Business www.ebusinesserp.com

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { createRoot } from "react-dom/client";
import {
    App, Button, Card, DatePicker, Divider, Form, Input, Radio, Select,
    Space, Typography, Upload, message, Alert, Tag, Row, Col, Spin,
    Checkbox, InputNumber, Empty, Result, Tooltip, Progress, Modal, Tabs, Badge,
    Dropdown, Avatar, Timeline, Descriptions, Popover, List
} from "antd";
import {
    EnvironmentOutlined, ClockCircleOutlined, DollarOutlined, UploadOutlined,
    SearchOutlined, FilterOutlined, RocketOutlined, TeamOutlined, SafetyOutlined,
    ArrowRightOutlined, UserOutlined, FileTextOutlined, QuestionCircleOutlined,
    CheckCircleOutlined, LeftOutlined, RightOutlined, MailOutlined, PhoneOutlined,
    ExclamationCircleOutlined, WarningOutlined, InfoCircleOutlined, LockOutlined,
    LoginOutlined, LogoutOutlined, DashboardOutlined, BellOutlined, EditOutlined,
    EyeOutlined, ProfileOutlined, KeyOutlined, ReloadOutlined,
    CalendarOutlined, CameraOutlined, IdcardOutlined, HeartOutlined,
    FilePdfOutlined, SafetyCertificateOutlined
} from '@ant-design/icons';
import "antd/dist/reset.css";
import "./Careers.css";
import StepProgress from "../shared/StepProgress";
import dayjs from "dayjs";
import { rules as vRules, normalizers as vNorm, helpers as vHelp } from "../shared/formRules";
import PhoneField from "../shared/PhoneField";

const { Title, Text, Paragraph } = Typography;

const ETHNICITY_OPTIONS = [
    "NZ European", "Māori", "Samoan", "Cook Islands Māori", "Tongan", "Niuean",
    "Fijian", "Tokelauan", "Tuvaluan", "Kiribati", "Papua New Guinean",
    "Solomon Islander", "Vanuatuan", "Filipino", "Chinese", "Indian",
    "Southeast Asian", "Korean", "Japanese", "Sri Lankan", "African",
    "Middle Eastern", "Latin American", "British / Irish", "Australian",
    "South African", "Other European", "Other"
];

const AGE_RANGE_OPTIONS = ["Under 18", "18-24", "25-34", "35-44", "45-54", "55-64", "65+", "Prefer not to say"];
const GENDER_OPTIONS = ["Male", "Female", "Other", "Prefer not to say"];
const EMPLOYMENT_STATUS_OPTIONS = ["Employed - Full Time", "Employed - Part Time", "Employed - Casual", "Self-Employed", "Unemployed", "Student", "Retired", "Other"];
const ID_TYPE_OPTIONS = ["Drivers Licence", "Passport", "Birth Certificate"];

// Job ad image (Oct 2026 fix). `src` is the one public URL the server builds
// (/api/public/vacancies/{id}/job-ad-image?v=...). No URL, or a file that fails to
// load, shows the blue rocket placeholder — never a broken image.
function JobAdImage({ src, alt, height }) {
    const [failed, setFailed] = useState(false);
    useEffect(() => { setFailed(false); }, [src]);
    const style = height ? { height } : undefined;
    if (!src || failed) {
        return <div className="job-card-placeholder" style={style}><RocketOutlined className="placeholder-icon" /></div>;
    }
    return (
        <div className="job-card-image-wrapper" style={style}>
            <img alt={alt || "Job advertisement"} src={src} className="job-card-image" onError={() => setFailed(true)} />
        </div>
    );
}

async function fetchJson(url, opts = {}) {
    const res = await fetch(url, { headers: { "Content-Type": "application/json" }, ...opts });
    if (!res.ok) {
        const errorText = await res.text();
        let errorObj;
        try { errorObj = JSON.parse(errorText); } catch { errorObj = { error: errorText || `HTTP ${res.status}` }; }
        throw errorObj;
    }
    return res.status === 204 ? null : await res.json();
}

// ==========================================================================
// NOTIFICATION BELL — candidate-panel notifications (NEW, notifications build)
//
// Polls /api/public/my-notifications so applicants finally see every ATS stage
// move and onboarding milestone in the careers panel itself. Notification Url
// values are HRIS app-relative (e.g. /my-onboarding), so clicks are resolved
// against the app base derived from the current page path (path-base safe).
// ==========================================================================
// Candidate-experience build: per-step validation lists (hidden conditional
// fields are unregistered and pass automatically).
// Verification step (re-hire) — validated when leaving the step; the division/
// location fields are only required when the candidate answers "Yes".
// ALL verification fields are compulsory for previous staff (client decision,
// 30 Jul 2026) — role, period and notes feed the manager's re-hire check.
const REHIRE_FIELDS = ['rehire_isPreviousStaff', 'rehire_previousLocation', 'rehire_previousRole', 'rehire_approxPeriod', 'rehire_notes'];
// Per-question detail keys ("<field>Details") replace the old shared
// section1Details / section2Details / otherDetails boxes — every Yes answer
// now gets its own required explanation (client request, 30 Jul 2026).
const HEALTH_FIELDS = ['health_gradualProcess', 'health_gradualProcessDetails', 'health_ouseHistory', 'health_ouseHistoryDetails', 'health_hearingLoss', 'health_hearingLossDetails', 'health_migraines', 'health_migrainesDetails', 'health_painProblems', 'health_painProblemsDetails', 'health_respiratory', 'health_respiratoryDetails', 'health_diabetes', 'health_diabetesDetails', 'health_epilepsy', 'health_epilepsyDetails', 'health_accClaim', 'health_accClaimDetails', 'health_standingIssues', 'health_standingIssuesDetails', 'health_medication', 'health_medicationDetails', 'health_preExisting', 'health_preExistingDetails', 'health_daysOff', 'health_healthImpact', 'health_healthImpactDetails', 'health_preEmploymentMedical', 'health_preEmploymentMedicalDetails', 'health_ongoingMedical', 'health_ongoingMedicalDetails', 'health_specialNeeds', 'health_specialNeedsDetail', 'health_declaration'];
const WA_FIELDS = ['wa_nzCitizen', 'wa_permanentResident', 'wa_visaAuthority', 'wa_visaAuthorityDetails', 'wa_hasDriverLicence', 'wa_licenceClass', 'wa_licenceNumber', 'wa_cardVersion', 'wa_demeritPoints', 'wa_demeritDetails', 'wa_hasCoa', 'wa_coaNumber', 'wa_coaExpiry'];
// Criminal History step — validated when leaving the step for Review & Submit
// (hidden conditional detail boxes are unregistered and pass automatically).
const CRIM_FIELDS = ['crim_criminalRecord', 'crim_criminalDetails', 'crim_coaCancelled', 'crim_coaCancelledDetails', 'crim_dishonestyConviction', 'crim_dishonestyConvictionDetails', 'crim_penalDetention', 'crim_penalDetentionDetails', 'crim_pendingCharges', 'crim_pendingChargesDetails', 'crim_gangAssociation', 'crim_gangAssociationDetails', 'crim_declaration', 'retention_consent'];
// SUBMIT FIX (Oct 2026): form.submit() validates every registered field, including
// the hidden steps, and used to fail silently. Each field name maps to its wizard
// step so onFinishFailed can name the step + field and jump straight to it.
const STEP_TITLES = ['Personal Information', 'Work Authorisation & Licences', 'Documents', 'Verification', 'Screening Questions', 'Health & Task Ability', 'Criminal History & Declarations', 'Review & Submit'];
function stepForField(name) {
    const n = Array.isArray(name) ? name[0] : name;
    if (typeof n !== 'string') return 0;
    if (n.startsWith('wa_')) return 1;
    if (n === 'coverLetter' || n === 'identificationType') return 2;
    if (n.startsWith('rehire_')) return 3;
    if (n.startsWith('screening_')) return 4;
    if (n.startsWith('health_')) return 5;
    if (n.startsWith('crim_') || n === 'retention_consent') return 6;
    return 0;
}
// Employment history & references are OPTIONAL at application time (client
// decision, 30 Jul 2026) — collected/completed in the onboarding portal.
// Secondary employment is not asked on Careers at all (portal-only question).
// EH_FIELDS removed (client review, Aug 2026): Employment History & References
// left the application — it is collected in My Onboarding, unlocked when a
// Reference Check requests candidate input.

// ============================================================================
// PAPER-FORM QUESTION ROW — question text on the left, compact Yes/No answer
// box right-aligned so every answer sits in one straight column (matches the
// printed Armourguard forms). When the trigger answer is given (usually Yes),
// a required detail box opens full-width directly beneath the question.
// Module-scope on purpose: defining it inside the component would remount the
// detail input on every keystroke and drop focus.
// ============================================================================
// Question row layout (wrap / label / answer) lives in Careers.css as .careers-qrow*, so phones can stack it.
const QROW_DETAIL = { padding: '10px 12px 2px 12px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' };
const QROW_SUBGRID = { padding: '10px 12px 0 12px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' };
const QROW_GROUP = { border: '1px solid #f0f0f0', borderBottom: 'none', borderRadius: 8, overflow: 'hidden', marginBottom: 16 };

// `notice` renders an inline warning under the question; `highlight` marks the answer box in error state.
function YNRow({ label, name, value, required = true, options = ['Yes', 'No'], detail = true, detailName, detailLabel = 'Please provide details', detailOn = 'Yes', detailRows = 2, detailPlaceholder, detailRequired = true, notice, highlight = false }) {
    const showDetail = detail && value === detailOn;
    return (
        <>
            <div className="careers-qrow">
                <span className="careers-qrow-label">{required && <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>}{label}</span>
                <Form.Item name={name} rules={required ? [{ required: true, message: 'Required' }] : undefined} className="careers-qrow-answer" validateStatus={highlight ? 'error' : undefined}>
                    <Select allowClear placeholder="Select">{options.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}</Select>
                </Form.Item>
            </div>
            {notice && (
                <div style={QROW_DETAIL}>
                    <Alert type={highlight ? 'error' : 'warning'} showIcon message={notice} style={{ marginBottom: 10 }} />
                </div>
            )}
            {showDetail && (
                <div style={QROW_DETAIL}>
                    <Form.Item label={detailLabel} name={detailName || `${name}Details`} rules={detailRequired ? [{ required: true, message: 'Required' }] : undefined} style={{ marginBottom: 10 }}>
                        <Input.TextArea rows={detailRows} placeholder={detailPlaceholder} />
                    </Form.Item>
                </div>
            )}
        </>
    );
}

// ============================================================================
// REVIEW & SUBMIT STEP (client request, 01 Sep 2026) — final wizard step.
// Read-only preview of every answer, grouped by step; each group carries an
// Edit button that jumps back (via goToStep) to the step it came from. The
// Submit button only appears on this step, below the preview.
// Module-scope for the same reason as YNRow: no remounts on re-render.
// ============================================================================
const RVW_ROW = { display: 'flex', gap: 16, padding: '8px 12px', borderBottom: '1px solid #f0f0f0', fontSize: 13.5 };
const RVW_LABEL = { flex: 1.4, color: '#64748b', lineHeight: 1.45 };
const RVW_VALUE = { flex: 1, color: '#1e293b', fontWeight: 500, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', textAlign: 'right' };
const RVW_GROUP = { border: '1px solid #f0f0f0', borderBottom: 'none', borderRadius: 8, overflow: 'hidden' };

// Anything renderable → display string; empty-ish → null so rows can flag it.
const rvwVal = (v) => {
    if (v === undefined || v === null || v === '') return null;
    if (Array.isArray(v)) return v.length ? v.join(', ') : null;
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'object' && v.$d) return v.format('DD/MM/YYYY');   // dayjs
    return String(v);
};

function RvwRow({ label, value, missing = false }) {
    const shown = rvwVal(value);
    return (
        <div style={RVW_ROW}>
            <span style={RVW_LABEL}>{label}</span>
            <span style={shown ? RVW_VALUE : { ...RVW_VALUE, fontWeight: 400, fontStyle: 'italic', color: missing ? '#cf1322' : '#94a3b8' }}>
                {shown || (missing ? 'Missing — please complete' : 'Not provided')}
            </span>
        </div>
    );
}

function RvwSection({ title, icon, step, onEdit, children }) {
    return (
        <Card size="small" style={{ marginBottom: 16 }}
            title={<Space>{icon}<span>{title}</span></Space>}
            extra={<Button type="link" size="small" icon={<EditOutlined />} onClick={() => onEdit(step)}>Edit</Button>}>
            <div style={RVW_GROUP}>{children}</div>
        </Card>
    );
}

// Question labels for the read-only Health / Criminal preview rows — kept 1:1
// with the YNRow labels on their steps. detail = the per-question detail field.
// Medical-examination consents (Task Ability Q5 / Q6): at least one must be Yes (client, Nov 2026).
// Same text in My Onboarding and on the server (apply endpoint).
const MEDICAL_CONSENT_MSG = 'At least one of questions 5 and 6 must be answered Yes. Your consent is only used if a medical examination is needed.';

const HEALTH_REVIEW_ROWS = [
    { label: '1) Have you had an injury or medical condition caused by gradual process, disease or infection (e.g. occupational overuse syndrome, hearing loss, sensitivity to chemicals, shift work disorder), which the tasks of this job may aggravate or contribute to?', name: 'health_gradualProcess', detail: 'health_gradualProcessDetails' },
    { label: 'Do you have a history of occupational overuse syndrome (e.g. tennis elbow)?', name: 'health_ouseHistory', detail: 'health_ouseHistoryDetails' },
    { label: 'Have you had any form of hearing loss at any time?', name: 'health_hearingLoss', detail: 'health_hearingLossDetails' },
    { label: 'Have you had a history of migraines, headaches, or other temporary debilitating ailment?', name: 'health_migraines', detail: 'health_migrainesDetails' },
    { label: 'Have you suffered pain or other problems in your wrist, forearm, elbow, shoulders, neck etc?', name: 'health_painProblems', detail: 'health_painProblemsDetails' },
    { label: 'Do you suffer from any respiratory problems (eg. asthma)?', name: 'health_respiratory', detail: 'health_respiratoryDetails' },
    { label: 'Diabetes?', name: 'health_diabetes', detail: 'health_diabetesDetails' },
    { label: 'Seizures or epilepsy?', name: 'health_epilepsy', detail: 'health_epilepsyDetails' },
    { label: 'Have you ever made a claim to ACC for any injury in the past 5 years?', name: 'health_accClaim', detail: 'health_accClaimDetails' },
    { label: 'Do you have any issues that will prevent you from standing or walking for extended periods?', name: 'health_standingIssues', detail: 'health_standingIssuesDetails' },
    { label: 'Are you presently receiving medical treatment requiring you to take any form of medication?', name: 'health_medication', detail: 'health_medicationDetails' },
    { label: 'Do you, or have you, had any issues with daily tasks as a result of an injury in the past (i.e. a pre existing condition)?', name: 'health_preExisting', detail: 'health_preExistingDetails' },
    { label: '3) How many days off work due to illness, have you had in the past 12 months?', name: 'health_daysOff' },
    { label: '4) Do you have any health related issues that may impact on your ability to perform the tasks for the position you are applying for?', name: 'health_healthImpact', detail: 'health_healthImpactDetails' },
    { label: '5) Do you consent to undergo a medical examination as part of a pre-employment process if required?', name: 'health_preEmploymentMedical', detail: 'health_preEmploymentMedicalDetails' },
    { label: '6) If successful in your application for employment, do you consent to undergo a medical examination at any time during your employment if required by the company?', name: 'health_ongoingMedical', detail: 'health_ongoingMedicalDetails' },
    { label: '7) Do you have any special needs in the workplace we should know about?', name: 'health_specialNeeds', detail: 'health_specialNeedsDetail' }
];
const CRIM_REVIEW_ROWS = [
    { label: 'Do you have a criminal record (except those in terms of the NZ Criminal Records Clean Slate Act 2004 can be withheld) that may impact on your ability to do this job, including obtaining a Certificate of Approval (COA) or similar?', name: 'crim_criminalRecord', detail: 'crim_criminalDetails' },
    { label: 'Have you ever held a security guard\'s licence or a Certificate of Approval licence that has been cancelled by the Registrar at any time within the last 5 years?', name: 'crim_coaCancelled', detail: 'crim_coaCancelledDetails' },
    { label: 'Have you ever been convicted of a crime involving dishonesty or a specified offence at any time?', name: 'crim_dishonestyConviction', detail: 'crim_dishonestyConvictionDetails' },
    { label: 'Have you ever been detained in a penal institution or jail at any time?', name: 'crim_penalDetention', detail: 'crim_penalDetentionDetails' },
    { label: 'Are you awaiting the hearing of charges in a civil or criminal court of law?', name: 'crim_pendingCharges', detail: 'crim_pendingChargesDetails' },
    { label: 'Are you currently or were you previously a member of, or otherwise involved, affiliated or associated with an NZ Gang or Motorcycle Club?', name: 'crim_gangAssociation', detail: 'crim_gangAssociationDetails' }
];

// App base = current path minus the /careers segment. HARDENED (27 Aug
// 2026): when the portal is served at the site root ('/'), the old strip
// left APP_BASE = '/', so links became '//my-onboarding' — a protocol-
// relative URL the browser reads as a DOMAIN called 'my-onboarding'.
// Trailing slashes are now trimmed so the base is always '' or '/prefix'.
const APP_BASE = (() => {
    if (typeof window === 'undefined') return '';
    let b = window.location.pathname.replace(/\/careers.*$/i, '');
    b = b.replace(/\/+$/, '');
    return b;
})();

function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(false);

    const loadCount = async () => {
        try {
            const d = await fetchJson("/api/public/my-notifications/unread-count");
            setUnread(d?.unread || 0);
        } catch { /* silent — badge just stays put */ }
    };

    const loadList = async () => {
        setLoading(true);
        try {
            const d = await fetchJson("/api/public/my-notifications?page=1&pageSize=15");
            setItems(d?.items || []);
            setUnread(d?.unread || 0);
        } catch { /* silent */ }
        finally { setLoading(false); }
    };

    useEffect(() => {
        loadCount();
        const t = setInterval(loadCount, 60000);   // 60s badge poll
        return () => clearInterval(t);
    }, []);

    const handleOpenChange = (v) => {
        setOpen(v);
        if (v) loadList();
    };

    const markRead = async (n) => {
        if (!n.isRead) {
            try { await fetchJson(`/api/public/my-notifications/${n.id}/read`, { method: "POST" }); } catch { }
            setItems(prev => prev.map(x => x.id === n.id ? { ...x, isRead: true } : x));
            setUnread(u => Math.max(0, u - 1));
        }
    };

    const markAll = async () => {
        try { await fetchJson("/api/public/my-notifications/read-all", { method: "POST" }); } catch { }
        setItems(prev => prev.map(x => ({ ...x, isRead: true })));
        setUnread(0);
    };

    const openNotification = (n) => {
        markRead(n);
        if (n.url) {
            // "/careers" stays in-panel; HRIS urls (e.g. /my-onboarding) get the app base.
            if (/^\/careers/i.test(n.url)) { setOpen(false); return; }
            window.location.href = APP_BASE + n.url;
        }
    };

    const timeAgo = (utc) => {
        const mins = Math.floor((Date.now() - new Date(utc).getTime()) / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        return new Date(utc).toLocaleDateString('en-NZ');
    };

    const content = (
        <div style={{ width: 360, maxHeight: 420, overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 4px 8px 4px', borderBottom: '1px solid #f0f0f0' }}>
                <Text strong>Notifications</Text>
                {unread > 0 && <Button type="link" size="small" onClick={markAll}>Mark all as read</Button>}
            </div>
            <List
                loading={loading}
                dataSource={items}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notifications yet" /> }}
                renderItem={(n) => (
                    <List.Item
                        onClick={() => openNotification(n)}
                        style={{ cursor: 'pointer', padding: '10px 6px', background: n.isRead ? '#fff' : '#eff6ff', borderRadius: 6, marginTop: 4 }}>
                        <List.Item.Meta
                            avatar={<Badge dot={!n.isRead}><BellOutlined style={{ fontSize: 18, color: '#1e3a5f' }} /></Badge>}
                            title={<span style={{ fontWeight: n.isRead ? 400 : 600 }}>{n.title}</span>}
                            description={
                                <>
                                    <div style={{ fontSize: 12.5, color: '#475569' }}>{n.message}</div>
                                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{timeAgo(n.createdUtc)}</div>
                                </>
                            } />
                    </List.Item>
                )} />
        </div>
    );

    return (
        <Popover content={content} trigger="click" placement="bottomRight"
            open={open} onOpenChange={handleOpenChange} overlayStyle={{ zIndex: 1200 }}>
            <Badge count={unread} size="small" offset={[-2, 4]}>
                <Button type="text" icon={<BellOutlined style={{ fontSize: 18 }} />} style={{ padding: '4px 8px' }} />
            </Badge>
        </Popover>
    );
}

// ==========================================================================
// AUTH MODAL — Register / Login / Verify / Forgot Password
// ==========================================================================
function AuthModal({ visible, onClose, onAuthSuccess, initialMode = "login" }) {
    const [mode, setMode] = useState(initialMode); // login, register, verify, forgot, reset
    const [loading, setLoading] = useState(false);
    const [pendingEmail, setPendingEmail] = useState("");
    const [form] = Form.useForm();

    useEffect(() => { if (visible) { setMode(initialMode); form.resetFields(); } }, [visible, initialMode]);

    async function handleRegister(vals) {
        setLoading(true);
        try {
            const res = await fetchJson("/api/public/register", {
                method: "POST",
                body: JSON.stringify({
                    firstName: vals.firstName,
                    lastName: vals.lastName,
                    email: vals.email,
                    password: vals.password,
                    phone: vals.phone || null
                })
            });
            message.success(res.message || "Registration successful!");
            setPendingEmail(vals.email);
            setMode("verify");
            form.resetFields();
        } catch (err) {
            message.error(err.error || "Registration failed");
        } finally { setLoading(false); }
    }

    async function handleVerify(vals) {
        setLoading(true);
        try {
            const res = await fetchJson("/api/public/verify-email", {
                method: "POST",
                body: JSON.stringify({ email: pendingEmail, code: vals.code })
            });
            message.success(res.message || "Email verified!");
            onAuthSuccess();
            onClose();
        } catch (err) {
            message.error(err.error || "Verification failed");
        } finally { setLoading(false); }
    }

    async function handleResendCode() {
        setLoading(true);
        try {
            await fetchJson("/api/public/resend-verification", {
                method: "POST",
                body: JSON.stringify({ email: pendingEmail })
            });
            message.success("New code sent! Check your email.");
        } catch (err) {
            message.error(err.error || "Failed to resend");
        } finally { setLoading(false); }
    }

    async function handleLogin(vals) {
        setLoading(true);
        try {
            const res = await fetchJson("/api/public/candidate-login", {
                method: "POST",
                body: JSON.stringify({ email: vals.email, password: vals.password })
            });
            message.success(`Welcome back, ${res.displayName}!`);
            onAuthSuccess();
            onClose();
        } catch (err) {
            if (err.error === "verify_required") {
                setPendingEmail(vals.email);
                setMode("verify");
                message.warning("Please verify your email first.");
            } else {
                message.error(err.error || "Login failed");
            }
        } finally { setLoading(false); }
    }

    async function handleForgotPassword(vals) {
        setLoading(true);
        try {
            await fetchJson("/api/public/forgot-password", {
                method: "POST",
                body: JSON.stringify({ email: vals.email })
            });
            message.success("If an account exists, a reset code has been sent.");
            setPendingEmail(vals.email);
            setMode("reset");
            form.resetFields();
        } catch (err) {
            message.error(err.error || "Failed");
        } finally { setLoading(false); }
    }

    async function handleResetPassword(vals) {
        setLoading(true);
        try {
            await fetchJson("/api/public/reset-password", {
                method: "POST",
                body: JSON.stringify({ email: pendingEmail, code: vals.code, newPassword: vals.newPassword })
            });
            message.success("Password reset! You can now login.");
            setMode("login");
            form.resetFields();
        } catch (err) {
            message.error(err.error || "Reset failed");
        } finally { setLoading(false); }
    }

    // "Code not received" on the reset screen (client UAT, Oct 2026) — re-issues the reset code.
    async function handleResendResetCode() {
        setLoading(true);
        try {
            await fetchJson("/api/public/forgot-password", {
                method: "POST",
                body: JSON.stringify({ email: pendingEmail })
            });
            message.success("A new reset code has been sent. Please also check your Junk / Spam folder.", 6);
        } catch (err) {
            message.error(err.error || "Failed to resend the code");
        } finally { setLoading(false); }
    }

    const junkNote = (
        <div style={{ marginTop: 6, fontSize: 12 }}>
            The email comes from <strong>hris@armourguard.co.nz</strong> and can land in your <strong>Junk / Spam</strong> folder — please check there if it has not arrived within a minute.
        </div>
    );

    const titles = {
        login: "Sign In to Your Account",
        register: "Create Your Account",
        verify: "Verify Your Email",
        forgot: "Forgot Password",
        reset: "Reset Password"
    };

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            maskClosable={false}
            keyboard={false}
            title={
                <Space>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg, #1e3a5f, #2d5a88)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {mode === "login" ? <LoginOutlined style={{ color: '#fff', fontSize: 18 }} /> :
                            mode === "register" ? <UserOutlined style={{ color: '#fff', fontSize: 18 }} /> :
                                mode === "verify" ? <MailOutlined style={{ color: '#fff', fontSize: 18 }} /> :
                                    <KeyOutlined style={{ color: '#fff', fontSize: 18 }} />}
                    </div>
                    <span>{titles[mode]}</span>
                </Space>
            }
            width={480}
            destroyOnClose
        >
            {/* ---- LOGIN ---- */}
            {mode === "login" && (
                <Form form={form} layout="vertical" onFinish={handleLogin} style={{ marginTop: 16 }}>
                    <Form.Item name="email" label="Email" rules={[{ required: true }, { type: "email", message: "Invalid email" }]}>
                        <Input prefix={<MailOutlined />} placeholder="your@email.com" size="large" autoComplete="email" />
                    </Form.Item>
                    <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Required' }]}>
                        <Input.Password prefix={<LockOutlined />} placeholder="Password" size="large" autoComplete="current-password" />
                    </Form.Item>
                    <div style={{ textAlign: 'right', marginBottom: 16 }}>
                        <Button type="link" size="small" onClick={() => { setMode("forgot"); form.resetFields(); }}>Forgot Password?</Button>
                    </div>
                    <Button type="primary" htmlType="submit" loading={loading} block size="large"
                        style={{ background: '#1e3a5f', borderColor: '#1e3a5f', height: 48 }}>
                        Sign In
                    </Button>
                    <Divider>New to Armourguard?</Divider>
                    <Button block size="large" onClick={() => { setMode("register"); form.resetFields(); }}
                        icon={<UserOutlined />} style={{ height: 44 }}>
                        Create an Account
                    </Button>
                </Form>
            )}

            {/* ---- REGISTER ---- */}
            {mode === "register" && (
                <Form form={form} layout="vertical" onFinish={handleRegister} style={{ marginTop: 16 }}>
                    <Alert message="Create an account to track your applications, save your profile, and apply faster."
                        type="info" showIcon style={{ marginBottom: 20 }} />
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="firstName" label="First Name" rules={[{ required: true, message: 'Required' }, vRules.personName]} normalize={vNorm.titleCase}>
                                <Input prefix={<UserOutlined />} placeholder="John" size="large" autoComplete="given-name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: 'Required' }, vRules.personName]} normalize={vNorm.titleCase}>
                                <Input prefix={<UserOutlined />} placeholder="Doe" size="large" autoComplete="family-name" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Required' }, { type: "email", message: 'Enter a valid email address' }]} normalize={vNorm.emailNorm}>
                        <Input prefix={<MailOutlined />} placeholder="your@email.com" size="large" autoComplete="email" />
                    </Form.Item>
                    <Form.Item name="phone" label="Mobile (Optional)" rules={[vRules.phoneValid]}>
                        <PhoneField size="large" />
                    </Form.Item>
                    <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Required' }, vRules.strongPassword]}
                        extra="At least 8 characters, with an uppercase letter, a number and a symbol (e.g. ! ? # @).">
                        <Input.Password prefix={<LockOutlined />} placeholder="Create a strong password" size="large" autoComplete="new-password" />
                    </Form.Item>
                    <Form.Item name="confirmPassword" label="Confirm Password"
                        dependencies={['password']}
                        rules={[{ required: true }, ({ getFieldValue }) => ({ validator(_, val) { return !val || getFieldValue('password') === val ? Promise.resolve() : Promise.reject('Passwords do not match'); } })]}>
                        <Input.Password prefix={<LockOutlined />} placeholder="Re-enter password" size="large" autoComplete="new-password" />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} block size="large"
                        style={{ background: '#1e3a5f', borderColor: '#1e3a5f', height: 48 }}>
                        Create Account
                    </Button>
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <Text type="secondary">Already have an account? </Text>
                        <Button type="link" onClick={() => { setMode("login"); form.resetFields(); }}>Sign In</Button>
                    </div>
                </Form>
            )}

            {/* ---- VERIFY ---- */}
            {mode === "verify" && (
                <div style={{ marginTop: 16 }}>
                    <Alert
                        message="Check Your Email"
                        description={<div>Please enter the 6-digit code we have sent to:<div style={{ marginTop: 4 }}><strong>{pendingEmail}</strong></div>{junkNote}</div>}
                        type="info" showIcon style={{ marginBottom: 24 }}
                    />
                    <Form form={form} layout="vertical" onFinish={handleVerify}>
                        <Form.Item name="code" label="Verification Code" rules={[{ required: true }, { len: 6, message: "Enter the 6-digit code" }]}>
                            <Input placeholder="000000" size="large" maxLength={6} autoComplete="one-time-code" inputMode="numeric"
                                readOnly onFocus={(e) => e.target.removeAttribute('readonly')}
                                style={{ textAlign: 'center', fontSize: 28, letterSpacing: 12, fontWeight: 'bold' }} />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading} block size="large"
                            style={{ background: '#1e3a5f', borderColor: '#1e3a5f', height: 48 }}>
                            Verify Email
                        </Button>
                    </Form>
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <Text type="secondary">Didn't receive the code? </Text>
                        <Button type="link" onClick={handleResendCode} loading={loading}>Resend Code</Button>
                    </div>
                </div>
            )}

            {/* ---- FORGOT ---- */}
            {mode === "forgot" && (
                <Form form={form} layout="vertical" onFinish={handleForgotPassword} style={{ marginTop: 16 }}>
                    <Alert message="Enter your email and we'll send a reset code." type="info" showIcon style={{ marginBottom: 20 }} />
                    <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Required' }, { type: "email", message: 'Enter a valid email address' }]} normalize={vNorm.emailNorm}>
                        <Input prefix={<MailOutlined />} placeholder="your@email.com" size="large" autoComplete="email" />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} block size="large"
                        style={{ background: '#1e3a5f', borderColor: '#1e3a5f', height: 48 }}>
                        Send Reset Code
                    </Button>
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <Button type="link" onClick={() => { setMode("login"); form.resetFields(); }}>Back to Sign In</Button>
                    </div>
                </Form>
            )}

            {/* ---- RESET ---- */}
            {mode === "reset" && (
                <Form form={form} layout="vertical" onFinish={handleResetPassword} style={{ marginTop: 16 }}>
                    <Alert
                        message="Enter Reset Code"
                        description={<div>Please enter the code we have sent to:<div style={{ marginTop: 4 }}><strong>{pendingEmail}</strong></div>{junkNote}</div>}
                        type="info" showIcon style={{ marginBottom: 20 }}
                    />
                    <Form.Item name="code" label="Reset Code" rules={[{ required: true, len: 6, message: 'Enter the 6-digit code' }]}>
                        <Input placeholder="000000" size="large" maxLength={6} autoComplete="one-time-code" inputMode="numeric"
                            readOnly onFocus={(e) => e.target.removeAttribute('readonly')}
                            style={{ textAlign: 'center', fontSize: 24, letterSpacing: 8 }} />
                    </Form.Item>
                    <Form.Item name="newPassword" label="New Password" rules={[{ required: true, message: 'Required' }, vRules.strongPassword]}
                        extra="At least 8 characters, with an uppercase letter, a number and a symbol (e.g. ! ? # @).">
                        <Input.Password prefix={<LockOutlined />} placeholder="Create a strong password" size="large" autoComplete="new-password" />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} block size="large"
                        style={{ background: '#1e3a5f', borderColor: '#1e3a5f', height: 48 }}>
                        Reset Password
                    </Button>
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <Text type="secondary">Code not received? </Text>
                        <Button type="link" onClick={handleResendResetCode} loading={loading}>Send a new code</Button>
                        <Button type="link" onClick={() => { setMode("login"); form.resetFields(); }}>Back to Sign In</Button>
                    </div>
                </Form>
            )}
        </Modal>
    );
}


// ==========================================================================
// MY APPLICATIONS DASHBOARD
// ==========================================================================
function MyApplicationsDashboard({ candidateInfo, onViewJob, onBrowseJobs }) {
    const [applications, setApplications] = useState([]);
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(false);
    // Client UAT (Oct 2026): applicants can withdraw their own application.
    const [withdrawTarget, setWithdrawTarget] = useState(null);
    const [withdrawReason, setWithdrawReason] = useState('');
    const [withdrawing, setWithdrawing] = useState(false);
    // The application just withdrawn — the modal turns into the "withdrawn" result page (Oct 2026).
    const [withdrawnDone, setWithdrawnDone] = useState(null);

    useEffect(() => { loadAll(); }, []);

    async function handleWithdraw() {
        if (!withdrawTarget) return;
        setWithdrawing(true);
        try {
            await fetchJson(`/api/public/my-applications/${withdrawTarget.id}/withdraw`, {
                method: 'POST',
                body: JSON.stringify({ reason: withdrawReason || null })
            });
            setWithdrawnDone(withdrawTarget);
            setWithdrawTarget(null);
            setWithdrawReason('');
            await loadAll();
        } catch (err) {
            message.error(err.error || 'Could not withdraw the application');
        } finally { setWithdrawing(false); }
    }

    async function loadAll() {
        setLoading(true);
        try {
            const [appData, draftData] = await Promise.all([
                fetchJson("/api/public/my-applications"),
                fetchJson("/api/public/my-drafts")
            ]);
            setApplications(appData || []);
            setDrafts(draftData || []);
        } catch (err) {
            message.error("Failed to load data");
        } finally { setLoading(false); }
    }

    async function handleDeleteDraft(draftId) {
        try {
            await fetchJson(`/api/public/draft/${draftId}`, { method: "DELETE" });
            setDrafts(prev => prev.filter(d => d.id !== draftId));
            message.success("Draft deleted");
        } catch { message.error("Failed to delete draft"); }
    }

    function getStageColor(code) {
        const map = {
            'APPLIED': 'blue', 'APPLICATION': 'blue', 'NEW': 'blue',
            'SCREEN': 'cyan', 'SCREENING': 'cyan',
            'INTERVIEW': 'purple', 'SHORTLIST': 'purple', 'SHORTLISTED': 'purple',
            'OFFER': 'gold', 'OFFERED': 'gold',
            'HIRED': 'green', 'ONBOARDING': 'green',
            'REJECTED': 'red', 'WITHDRAWN': 'red', 'DECLINED': 'red',
            'REHIRE_VERIFICATION': 'geekblue'   // shown as "Under internal verification"
        };
        return map[(code || '').toUpperCase()] || 'default';
    }

    function getStatusColor(status) {
        if (status === 'Active') return '#52c41a';
        if (status === 'Withdrawn') return '#ff4d4f';
        if (status === 'Hired') return '#1890ff';
        return '#d9d9d9';
    }

    return (
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 20px' }}>
            {/* Welcome card */}
            <Card style={{ marginBottom: 24, background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a88 100%)', border: 'none' }}>
                <Row align="middle" gutter={16}>
                    <Col>
                        <Avatar size={56} style={{ backgroundColor: 'rgba(255,255,255,0.2)', fontSize: 24 }}>
                            {candidateInfo?.firstName?.[0] || 'C'}
                        </Avatar>
                    </Col>
                    <Col flex={1}>
                        <Title level={4} style={{ color: '#fff', margin: 0 }}>
                            Welcome back, {candidateInfo?.firstName || 'Candidate'}!
                        </Title>
                        <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
                            {candidateInfo?.candidateNo} &middot; {candidateInfo?.email}
                        </Text>
                    </Col>
                    <Col>
                        <Button type="primary" ghost icon={<SearchOutlined />} onClick={onBrowseJobs}
                            style={{ borderColor: '#fff', color: '#fff' }}>
                            Browse Open Positions
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Stats */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={8}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 28, fontWeight: 700, color: '#1e3a5f' }}>{applications.length}</div>
                        <Text type="secondary">Total Applications</Text>
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 28, fontWeight: 700, color: '#52c41a' }}>
                            {applications.filter(a => a.status === 'Active').length}
                        </div>
                        <Text type="secondary">Active</Text>
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 28, fontWeight: 700, color: '#722ed1' }}>
                            {applications.filter(a => ['INTERVIEW', 'SHORTLIST', 'SHORTLISTED', 'OFFER', 'OFFERED'].includes((a.stageCode || '').toUpperCase())).length}
                        </div>
                        <Text type="secondary">In Progress</Text>
                    </Card>
                </Col>
            </Row>

            {/* Saved Drafts */}
            {drafts.length > 0 && (
                <Card
                    title={<Space><EditOutlined style={{ color: '#d48806' }} /><span>Saved Drafts</span><Tag color="orange">{drafts.length}</Tag></Space>}
                    style={{ marginBottom: 24 }}
                >
                    {drafts.map((draft) => (
                        <Card key={draft.id} size="small"
                            style={{ marginBottom: 12, borderLeft: '4px solid #faad14', background: '#fffbe6' }}
                        >
                            <Row align="middle" gutter={16}>
                                <Col flex={1}>
                                    <div>
                                        <Text strong style={{ fontSize: 15 }}>{draft.vacancyTitle}</Text>
                                        <Tag color="orange" style={{ marginLeft: 8 }}>Draft</Tag>
                                        {!draft.isVacancyOpen && <Tag color="red" style={{ marginLeft: 4 }}>Position Closed</Tag>}
                                    </div>
                                    <Space size="middle" style={{ marginTop: 4 }}>
                                        {draft.locationName && <Text type="secondary" style={{ fontSize: 12 }}><EnvironmentOutlined /> {draft.locationName}</Text>}
                                        <Text type="secondary" style={{ fontSize: 12 }}>Step {draft.lastStep + 1} of 4</Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Saved {new Date(draft.savedDate).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </Text>
                                    </Space>
                                </Col>
                                <Col>
                                    <Space>
                                        {draft.isVacancyOpen && (
                                            <Button type="primary" size="small" icon={<EditOutlined />}
                                                onClick={() => onViewJob(draft.vacancySlug)}
                                                style={{ background: '#d48806', borderColor: '#d48806' }}>
                                                Continue
                                            </Button>
                                        )}
                                        <Button size="small" danger onClick={() => handleDeleteDraft(draft.id)}>Delete</Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Card>
                    ))}
                </Card>
            )}

            {/* Applications list */}
            <Card
                title={<Space><FileTextOutlined style={{ color: '#1e3a5f' }} /><span>My Applications</span></Space>}
                extra={<Button icon={<ReloadOutlined />} onClick={loadAll} loading={loading} size="small">Refresh</Button>}
            >
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
                ) : applications.length === 0 ? (
                    <Empty
                        description={<span>You haven't applied to any positions yet</span>}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                        <Button type="primary" onClick={onBrowseJobs} icon={<SearchOutlined />}>Browse Open Positions</Button>
                    </Empty>
                ) : (
                    <div>
                        {applications.map((app) => (
                            <Card key={app.id} size="small"
                                style={{ marginBottom: 12, borderLeft: `4px solid ${getStatusColor(app.status)}` }}
                                hoverable
                            >
                                <Row align="middle" gutter={16}>
                                    <Col flex={1}>
                                        <div>
                                            <Text strong style={{ fontSize: 15 }}>{app.vacancyTitle}</Text>
                                            <Tag color={getStageColor(app.stageCode)} style={{ marginLeft: 8 }}>{app.stageName}</Tag>
                                        </div>
                                        <Space size="middle" style={{ marginTop: 4 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>{app.applicationNumber}</Text>
                                            {app.locationName && <Text type="secondary" style={{ fontSize: 12 }}><EnvironmentOutlined /> {app.locationName}</Text>}
                                            {app.employmentType && <Text type="secondary" style={{ fontSize: 12 }}><ClockCircleOutlined /> {app.employmentType}</Text>}
                                        </Space>
                                        {(() => {
                                            // Closed / withdrawn applications explain what happened instead of a bare status word
                                            const code = (app.stageCode || '').toUpperCase();
                                            const closed = app.status === 'Withdrawn' || app.status === 'Rejected' || ['WITHDRAWN', 'REJECTED', 'DECLINED'].includes(code);
                                            if (!closed) return null;
                                            const when = app.statusChangedUtc ? new Date(app.statusChangedUtc).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
                                            const fallback = (app.status === 'Withdrawn' || code === 'WITHDRAWN')
                                                ? `Your application was withdrawn from this vacancy by Armourguard People & Culture${when ? ` on ${when}` : ''}. If you believe this is in error or have any questions, please contact us at hris@armourguard.co.nz.`
                                                : `This application is no longer being progressed${when ? ` (updated ${when})` : ''}. Thank you for your interest in Armourguard — you are welcome to apply for other roles. Questions: hris@armourguard.co.nz.`;
                                            return (
                                                <div style={{ marginTop: 8, padding: '8px 12px', background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: 6, fontSize: 13, color: '#5c0011' }}>
                                                    {app.statusNote || fallback}
                                                </div>
                                            );
                                        })()}
                                    </Col>
                                    <Col>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>Applied</div>
                                            <div style={{ fontSize: 13, fontWeight: 500 }}>
                                                {new Date(app.appliedDate).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </div>
                                            <div>
                                                <Button type="link" size="small" icon={<FilePdfOutlined />}
                                                    style={{ padding: 0, marginTop: 4, fontSize: 12, color: '#1e3a5f' }}
                                                    onClick={(e) => { e.stopPropagation(); window.open(`/api/public/my-application/${app.id}/pdf`, '_blank'); }}>
                                                    View / print my application
                                                </Button>
                                            </div>
                                            {app.status === 'Active' && (
                                                <div>
                                                    <Button type="link" size="small" danger
                                                        style={{ padding: 0, marginTop: 2, fontSize: 12 }}
                                                        onClick={(e) => { e.stopPropagation(); setWithdrawReason(''); setWithdrawTarget(app); }}>
                                                        Withdraw application
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </Card>
                        ))}
                    </div>
                )}
            </Card>

            <Modal
                open={!!withdrawTarget}
                title="Withdraw this application?"
                onCancel={() => setWithdrawTarget(null)}
                onOk={handleWithdraw}
                okText="Yes — withdraw my application"
                okButtonProps={{ danger: true, loading: withdrawing }}
                cancelButtonProps={{ disabled: withdrawing }}
                destroyOnClose>
                <Paragraph>
                    You are about to withdraw your application for <strong>{withdrawTarget?.vacancyTitle}</strong> ({withdrawTarget?.applicationNumber}).
                    This cannot be undone from the portal — you would need to apply again. Your submitted details and documents
                    for this application will be deleted within 30 days.
                </Paragraph>
                <Form.Item label="Reason (optional)" style={{ marginBottom: 0 }}>
                    <Input.TextArea rows={3} value={withdrawReason} onChange={e => setWithdrawReason(e.target.value)}
                        placeholder="Let us know why, if you would like to" maxLength={500} />
                </Form.Item>
            </Modal>

            <Modal open={!!withdrawnDone} footer={null} onCancel={() => setWithdrawnDone(null)} destroyOnClose>
                <Result status="success" title="Application withdrawn"
                    subTitle={<>Your application for <strong>{withdrawnDone?.vacancyTitle}</strong> ({withdrawnDone?.applicationNumber}) has been withdrawn.
                        Any onboarding or offer links you received for it no longer work. Your submitted details and documents for it will be
                        deleted within 30 days. You are welcome to apply again while the role is open.</>}
                    extra={<Button type="primary" onClick={() => setWithdrawnDone(null)}>Back to My Applications</Button>} />
            </Modal>
        </div>
    );
}


// ==========================================================================
// MAIN CAREERS APP
// ==========================================================================
// ===== CAREERS HELP GUIDE =====
const CHelpFlowStep = ({ icon, label, color = '#eef2ff', borderColor = '#c7d2fe' }) => (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 64, height: 64, borderRadius: 16, background: color, border: `2px solid ${borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>{icon}</div>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', textAlign: 'center', maxWidth: 80 }}>{label}</span>
    </div>
);
const CHelpFlowArrow = () => <span style={{ fontSize: 20, color: '#94a3b8', margin: '0 4px', alignSelf: 'center' }}>›</span>;
const CHelpStatusBadge = ({ color, bg, children }) => <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color, background: bg, marginRight: 6 }}>{children}</span>;
const CHelpTipCard = ({ emoji, title, children }) => (
    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', marginBottom: 10 }}>
        <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 4 }}>{emoji} {title}</div>
        <div style={{ fontSize: 13, color: '#78350f' }}>{children}</div>
    </div>
);

const CareersHelpGuide = ({ open, onClose }) => {
    const [section, setSection] = useState('overview');

    const sections = [
        { key: 'overview', label: 'Overview', icon: '🏠' },
        { key: 'register', label: 'Register & Sign In', icon: '🔑' },
        { key: 'applying', label: 'How to Apply', icon: '📝' },
        { key: 'step1', label: 'Step 1: Personal Info', icon: '👤' },
        { key: 'step2', label: 'Step 2: Documents', icon: '📎' },
        { key: 'step3', label: 'Step 3: Screening', icon: '❓' },
        { key: 'drafts', label: 'Drafts & Progress', icon: '💾' },
        { key: 'dashboard', label: 'My Applications', icon: '📊' },
        { key: 'tips', label: 'Tips & FAQ', icon: '💡' },
    ];

    const renderContent = () => {
        switch (section) {
            case 'overview': return (
                <div>
                    <div style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', borderRadius: 16, padding: 32, textAlign: 'center', marginBottom: 24 }}>
                        <div style={{ fontSize: 40, marginBottom: 8 }}>🛡️</div>
                        <h2 style={{ margin: 0, color: '#1e40af', fontSize: 22 }}>Armourguard Careers Portal</h2>
                        <p style={{ margin: '8px 0 0', color: '#3730a3', fontSize: 14 }}>Your gateway to joining New Zealand's leading security team</p>
                    </div>
                    <h4 style={{ color: '#1e40af', marginBottom: 12 }}>Application Journey — At a Glance</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 4, marginBottom: 24, justifyContent: 'center' }}>
                        <CHelpFlowStep icon="🔍" label="Browse Jobs" color="#eef2ff" borderColor="#c7d2fe" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="📝" label="Register" color="#e0f2fe" borderColor="#7dd3fc" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="👤" label="Personal Info" color="#dcfce7" borderColor="#86efac" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="📎" label="Upload CV" color="#fef3c7" borderColor="#fcd34d" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="❓" label="Screening" color="#fce7f3" borderColor="#f9a8d4" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="✅" label="Submit!" color="#dcfce7" borderColor="#4ade80" />
                    </div>
                    <h4 style={{ color: '#475569', marginTop: 16 }}>What You'll Need</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: 16 }}>
                            <div style={{ fontSize: 20, marginBottom: 6 }}>📄</div>
                            <h4 style={{ color: '#166534', margin: '0 0 4px' }}>CV / Resume</h4>
                            <p style={{ fontSize: 12, color: '#15803d', margin: 0 }}>PDF or Word document up to 10MB</p>
                        </div>
                        <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 12, padding: 16 }}>
                            <div style={{ fontSize: 20, marginBottom: 6 }}>📸</div>
                            <h4 style={{ color: '#1e40af', margin: '0 0 4px' }}>Photo & ID (Optional)</h4>
                            <p style={{ fontSize: 12, color: '#3730a3', margin: 0 }}>Profile photo and identity document for verification</p>
                        </div>
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: 16 }}>
                            <div style={{ fontSize: 20, marginBottom: 6 }}>🔑</div>
                            <h4 style={{ color: '#92400e', margin: '0 0 4px' }}>Email Address</h4>
                            <p style={{ fontSize: 12, color: '#78350f', margin: 0 }}>Valid email for registration and OTP verification</p>
                        </div>
                        <div style={{ background: '#fce7f3', border: '1px solid #fbcfe8', borderRadius: 12, padding: 16 }}>
                            <div style={{ fontSize: 20, marginBottom: 6 }}>⏱️</div>
                            <h4 style={{ color: '#9d174d', margin: '0 0 4px' }}>5–10 Minutes</h4>
                            <p style={{ fontSize: 12, color: '#831843', margin: 0 }}>Average time to complete an application</p>
                        </div>
                    </div>
                    <CHelpTipCard emoji="💡" title="Save Your Progress">Register or sign in before you start — this lets you save drafts and return later to complete your application.</CHelpTipCard>
                </div>
            );
            case 'register': return (
                <div>
                    <h3 style={{ color: '#1e40af' }}>🔑 Register & Sign In</h3>
                    <p style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>Create an account to save drafts, track applications, and apply to multiple positions.</p>
                    <h4 style={{ color: '#475569' }}>Registration Flow</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 4, margin: '16px 0', justifyContent: 'center' }}>
                        <CHelpFlowStep icon="📧" label="Enter Email" color="#eef2ff" borderColor="#c7d2fe" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="🔢" label="Receive OTP" color="#e0f2fe" borderColor="#7dd3fc" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="✅" label="Verify Code" color="#dcfce7" borderColor="#86efac" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="👤" label="Set Profile" color="#fef3c7" borderColor="#fcd34d" />
                    </div>
                    <CHelpTipCard emoji="📧" title="OTP Verification">A one-time code is sent to your email address. Enter this code to verify your identity — no password needed!</CHelpTipCard>
                    <CHelpTipCard emoji="🔄" title="Returning Users">Already registered? Click "Sign In" and enter your email to receive a new OTP. Your previous applications and drafts will be waiting for you.</CHelpTipCard>
                    <h4 style={{ color: '#475569', marginTop: 16 }}>Benefits of Registering</h4>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <CHelpStatusBadge color="#166534" bg="#dcfce7">Save Drafts</CHelpStatusBadge>
                        <CHelpStatusBadge color="#1e40af" bg="#dbeafe">Track Applications</CHelpStatusBadge>
                        <CHelpStatusBadge color="#6b21a8" bg="#f3e8ff">Apply to Multiple Jobs</CHelpStatusBadge>
                        <CHelpStatusBadge color="#92400e" bg="#fef3c7">Resume Later</CHelpStatusBadge>
                    </div>
                </div>
            );
            case 'applying': return (
                <div>
                    <h3 style={{ color: '#1e40af' }}>📝 How to Apply</h3>
                    <p style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>The application form is split into 3 simple steps. You can navigate back and forth between steps freely.</p>
                    <h4 style={{ color: '#475569' }}>The 3-Step Process</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 4, margin: '16px 0', justifyContent: 'center' }}>
                        <CHelpFlowStep icon="👤" label="Personal Info" color="#eef2ff" borderColor="#c7d2fe" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="📎" label="Documents" color="#fef3c7" borderColor="#fcd34d" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="❓" label="Screening" color="#fce7f3" borderColor="#f9a8d4" />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 20 }}>
                        <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 12, padding: 16 }}>
                            <h4 style={{ color: '#1e40af', margin: '0 0 8px' }}>Step 1 — Personal Information</h4>
                            <p style={{ fontSize: 12, color: '#3730a3', margin: 0 }}>Name, email, phone, date of birth, gender, ethnicity, and employment details. Fields marked with a red asterisk (*) are required.</p>
                        </div>
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: 16 }}>
                            <h4 style={{ color: '#92400e', margin: '0 0 8px' }}>Step 2 — Documents & Identity</h4>
                            <p style={{ fontSize: 12, color: '#78350f', margin: 0 }}>Upload your CV/Resume (required), optional cover letter, profile photo, and identity verification document.</p>
                        </div>
                        <div style={{ background: '#fce7f3', border: '1px solid #fbcfe8', borderRadius: 12, padding: 16 }}>
                            <h4 style={{ color: '#9d174d', margin: '0 0 8px' }}>Step 3 — Screening Questions</h4>
                            <p style={{ fontSize: 12, color: '#831843', margin: 0 }}>Answer job-specific questions set by the hiring team. Some questions may appear based on your previous answers (conditional follow-ups).</p>
                        </div>
                    </div>
                    <CHelpTipCard emoji="✅" title="Progress Tracker">Each step shows a green tick when complete. The submit button only activates once all required fields across all 3 steps are filled in.</CHelpTipCard>
                </div>
            );
            case 'step1': return (
                <div>
                    <h3 style={{ color: '#1e40af' }}>👤 Step 1: Personal Information</h3>
                    <p style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>Provide your personal details. If you're signed in, some fields may be pre-filled from your profile.</p>
                    <h4 style={{ color: '#475569' }}>Required Fields</h4>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                        <CHelpStatusBadge color="#dc2626" bg="#fee2e2">First Name</CHelpStatusBadge>
                        <CHelpStatusBadge color="#dc2626" bg="#fee2e2">Last Name</CHelpStatusBadge>
                        <CHelpStatusBadge color="#dc2626" bg="#fee2e2">Email</CHelpStatusBadge>
                    </div>
                    <h4 style={{ color: '#475569' }}>Optional Fields</h4>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                        <CHelpStatusBadge color="#475569" bg="#f1f5f9">Phone</CHelpStatusBadge>
                        <CHelpStatusBadge color="#475569" bg="#f1f5f9">Date of Birth</CHelpStatusBadge>
                        <CHelpStatusBadge color="#475569" bg="#f1f5f9">Age Range</CHelpStatusBadge>
                        <CHelpStatusBadge color="#475569" bg="#f1f5f9">Gender</CHelpStatusBadge>
                        <CHelpStatusBadge color="#475569" bg="#f1f5f9">Ethnicity</CHelpStatusBadge>
                        <CHelpStatusBadge color="#475569" bg="#f1f5f9">Marital Status</CHelpStatusBadge>
                        <CHelpStatusBadge color="#475569" bg="#f1f5f9">Employment Status</CHelpStatusBadge>
                    </div>
                    <CHelpTipCard emoji="📧" title="Email Accuracy">Double-check your email address — this is how the hiring team will contact you. If you're registered, the email is locked to your account.</CHelpTipCard>
                </div>
            );
            case 'step2': return (
                <div>
                    <h3 style={{ color: '#1e40af' }}>📎 Step 2: Documents & Identity</h3>
                    <p style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>Upload your supporting documents. Your CV is required — other documents are optional but recommended.</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                        <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 12, padding: 16 }}>
                            <div style={{ fontSize: 20, marginBottom: 6 }}>📄</div>
                            <h4 style={{ color: '#dc2626', margin: '0 0 4px' }}>CV / Resume *</h4>
                            <p style={{ fontSize: 12, color: '#991b1b', margin: 0 }}>Required. PDF or Word, up to 10MB.</p>
                        </div>
                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: 16 }}>
                            <div style={{ fontSize: 20, marginBottom: 6 }}>✉️</div>
                            <h4 style={{ color: '#166534', margin: '0 0 4px' }}>Cover Letter</h4>
                            <p style={{ fontSize: 12, color: '#15803d', margin: 0 }}>Optional. Tell us why you're a great fit.</p>
                        </div>
                        <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 12, padding: 16 }}>
                            <div style={{ fontSize: 20, marginBottom: 6 }}>📸</div>
                            <h4 style={{ color: '#1e40af', margin: '0 0 4px' }}>Profile Photo</h4>
                            <p style={{ fontSize: 12, color: '#3730a3', margin: 0 }}>Optional. JPEG or PNG, up to 5MB.</p>
                        </div>
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: 16 }}>
                            <div style={{ fontSize: 20, marginBottom: 6 }}>🪪</div>
                            <h4 style={{ color: '#92400e', margin: '0 0 4px' }}>ID Document</h4>
                            <p style={{ fontSize: 12, color: '#78350f', margin: 0 }}>Optional. For identity verification (PDF or image).</p>
                        </div>
                    </div>
                    <CHelpTipCard emoji="💾" title="Auto-Save for Registered Users">If you're signed in, uploaded files are automatically saved with your draft. You won't need to re-upload them if you come back later.</CHelpTipCard>
                </div>
            );
            case 'step3': return (
                <div>
                    <h3 style={{ color: '#1e40af' }}>❓ Step 3: Screening Questions</h3>
                    <p style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>Each job may have specific questions set by the hiring team. These help assess your suitability for the role.</p>
                    <h4 style={{ color: '#475569' }}>Question Types You May See</h4>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                        <CHelpStatusBadge color="#1e40af" bg="#dbeafe">Text</CHelpStatusBadge>
                        <CHelpStatusBadge color="#6b21a8" bg="#f3e8ff">Yes / No</CHelpStatusBadge>
                        <CHelpStatusBadge color="#92400e" bg="#fef3c7">Dropdown</CHelpStatusBadge>
                        <CHelpStatusBadge color="#166534" bg="#dcfce7">Multi-Select</CHelpStatusBadge>
                        <CHelpStatusBadge color="#0e7490" bg="#cffafe">Date</CHelpStatusBadge>
                        <CHelpStatusBadge color="#9d174d" bg="#fce7f3">File Upload</CHelpStatusBadge>
                    </div>
                    <h4 style={{ color: '#475569', marginTop: 16 }}>Conditional / Follow-up Questions</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 4, margin: '16px 0', justifyContent: 'center' }}>
                        <CHelpFlowStep icon="❓" label="Answer Parent" color="#eef2ff" borderColor="#c7d2fe" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="🔗" label="Condition Met" color="#fef3c7" borderColor="#fcd34d" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="📋" label="Follow-up Appears" color="#fce7f3" borderColor="#f9a8d4" />
                    </div>
                    <p style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>Some questions only appear based on your answer to a previous question. These are highlighted with a yellow border and a "Follow-up" tag.</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: 16 }}>
                            <div style={{ fontWeight: 600, color: '#166534', marginBottom: 4 }}>🟢 Green Border</div>
                            <p style={{ fontSize: 12, color: '#15803d', margin: 0 }}>Question has been answered</p>
                        </div>
                        <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 12, padding: 16 }}>
                            <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: 4 }}>🔴 Red Border</div>
                            <p style={{ fontSize: 12, color: '#991b1b', margin: 0 }}>Required question — still needs an answer</p>
                        </div>
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: 16 }}>
                            <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 4 }}>🟡 Yellow Border</div>
                            <p style={{ fontSize: 12, color: '#78350f', margin: 0 }}>Conditional follow-up question</p>
                        </div>
                        <div style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
                            <div style={{ fontWeight: 600, color: '#475569', marginBottom: 4 }}>⬜ No Border</div>
                            <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Optional question</p>
                        </div>
                    </div>
                    <CHelpTipCard emoji="📊" title="Progress Bar">A progress bar shows how many required questions you've answered. Submit is only enabled once all required questions are complete.</CHelpTipCard>
                </div>
            );
            case 'drafts': return (
                <div>
                    <h3 style={{ color: '#1e40af' }}>💾 Drafts & Saving Progress</h3>
                    <p style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>Don't worry about losing your work — registered users can save drafts at any point during the application.</p>
                    <h4 style={{ color: '#475569' }}>How Drafts Work</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 4, margin: '16px 0', justifyContent: 'center' }}>
                        <CHelpFlowStep icon="📝" label="Start Filling" color="#eef2ff" borderColor="#c7d2fe" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="💾" label="Click Save Draft" color="#fef3c7" borderColor="#fcd34d" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="🚪" label="Come Back Later" color="#fce7f3" borderColor="#f9a8d4" />
                        <CHelpFlowArrow />
                        <CHelpFlowStep icon="✅" label="Resume & Submit" color="#dcfce7" borderColor="#4ade80" />
                    </div>
                    <CHelpTipCard emoji="⚠️" title="Must Be Signed In">Drafts are only available for registered and signed-in users. Guest applicants cannot save progress.</CHelpTipCard>
                    <CHelpTipCard emoji="📁" title="Files Saved">Uploaded files (CV, cover letter, photo, ID) are saved with your draft. No need to re-upload when you return.</CHelpTipCard>
                    <CHelpTipCard emoji="🗑️" title="One Draft Per Job">You can only have one draft per job vacancy. Saving again overwrites the previous draft. Once submitted, the draft is cleared.</CHelpTipCard>
                </div>
            );
            case 'dashboard': return (
                <div>
                    <h3 style={{ color: '#1e40af' }}>📊 My Applications Dashboard</h3>
                    <p style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>Track all your submitted applications and drafts in one place. Access your dashboard from the top navigation bar.</p>
                    <h4 style={{ color: '#475569' }}>Application Statuses</h4>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                        <CHelpStatusBadge color="#92400e" bg="#fef3c7">Draft</CHelpStatusBadge>
                        <CHelpStatusBadge color="#1e40af" bg="#dbeafe">Submitted</CHelpStatusBadge>
                        <CHelpStatusBadge color="#6b21a8" bg="#f3e8ff">Under Review</CHelpStatusBadge>
                        <CHelpStatusBadge color="#0e7490" bg="#cffafe">Interview</CHelpStatusBadge>
                        <CHelpStatusBadge color="#166534" bg="#dcfce7">Offered</CHelpStatusBadge>
                        <CHelpStatusBadge color="#dc2626" bg="#fee2e2">Declined</CHelpStatusBadge>
                    </div>
                    <CHelpTipCard emoji="📋" title="View Submitted Applications">Click on any submitted application to view the details. You can also download a PDF copy of your submitted application.</CHelpTipCard>
                    <CHelpTipCard emoji="✏️" title="Continue Drafts">Draft applications show a "Continue" button — click it to pick up right where you left off.</CHelpTipCard>
                </div>
            );
            case 'tips': return (
                <div>
                    <h3 style={{ color: '#1e40af' }}>💡 Tips & FAQ</h3>
                    <CHelpTipCard emoji="📄" title="Best CV Format">PDF format is recommended for your CV as it preserves formatting across all devices. Keep it under 10MB.</CHelpTipCard>
                    <CHelpTipCard emoji="⏰" title="Application Deadlines">Check the closing date on each job listing. Applications cannot be submitted after the closing date.</CHelpTipCard>
                    <CHelpTipCard emoji="🔄" title="Multiple Applications">You can apply to as many open positions as you like. Each application is tracked separately in your dashboard.</CHelpTipCard>
                    <CHelpTipCard emoji="🔒" title="Data Privacy">Your personal information is stored securely and only shared with the Armourguard hiring team for recruitment purposes.</CHelpTipCard>
                    <CHelpTipCard emoji="📱" title="Mobile Friendly">The careers portal works on all devices. You can start on your phone and continue on your computer — your draft will be saved.</CHelpTipCard>
                    <h4 style={{ color: '#475569', marginTop: 20 }}>Common Questions</h4>
                    <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16 }}>
                        <div style={{ marginBottom: 12 }}><strong style={{ color: '#1e40af' }}>Q: I didn't receive my OTP code?</strong><p style={{ fontSize: 12, color: '#475569', margin: '4px 0 0' }}>Check your spam/junk folder. The email comes from hris@armourguard.co.nz. If it still doesn't arrive, try again after 60 seconds.</p></div>
                        <div style={{ marginBottom: 12 }}><strong style={{ color: '#1e40af' }}>Q: Can I edit my application after submitting?</strong><p style={{ fontSize: 12, color: '#475569', margin: '4px 0 0' }}>No — once submitted, your application is locked. Make sure everything is correct before clicking Submit.</p></div>
                        <div style={{ marginBottom: 0 }}><strong style={{ color: '#1e40af' }}>Q: I applied before — can I apply for the same job again?</strong><p style={{ fontSize: 12, color: '#475569', margin: '4px 0 0' }}>You can have one active application per vacancy. If you withdraw it, you can apply again while the role is open; otherwise you'll see a notice that you've already applied.</p></div>
                    </div>
                </div>
            );
            default: return null;
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={820}
            centered
            zIndex={12000}
            bodyStyle={{ padding: 0 }}
            maskStyle={{ backdropFilter: 'blur(4px)' }}
            destroyOnClose
        >
            <div style={{ display: 'flex', minHeight: '70vh', maxHeight: '70vh' }}>
                {/* Sidebar */}
                <div style={{ width: 200, flexShrink: 0, background: '#f8fafc', borderRight: '1px solid #e2e8f0', overflowY: 'auto', padding: '20px 0' }}>
                    <div style={{ padding: '0 16px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 18 }}>?</div>
                        <div><div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Help Guide</div><div style={{ fontSize: 11, color: '#64748b' }}>Careers Portal</div></div>
                    </div>
                    {sections.map(s => (
                        <div key={s.key} onClick={() => setSection(s.key)}
                            style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: section === s.key ? 600 : 400, color: section === s.key ? '#1e40af' : '#475569', background: section === s.key ? '#dbeafe' : 'transparent', borderLeft: section === s.key ? '3px solid #1e40af' : '3px solid transparent', transition: 'all 0.15s' }}>
                            <span>{s.icon}</span> {s.label}
                        </div>
                    ))}
                </div>
                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 28 }}>
                    {renderContent()}
                </div>
            </div>
        </Modal>
    );
};

function CareersApp() {
    const [loading, setLoading] = useState(false);
    const [vacancies, setVacancies] = useState([]);
    const [selected, setSelected] = useState(null);
    const [applying, setApplying] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [locationFilter, setLocationFilter] = useState("all");
    const [form] = Form.useForm();
    const [filesByQ, setFilesByQ] = useState({});
    const [currentStep, setCurrentStep] = useState(0);
    const [screeningAnswers, setScreeningAnswers] = useState({});
    const [submissionSuccess, setSubmissionSuccess] = useState(null);
    const [duplicateApplication, setDuplicateApplication] = useState(null);
    const [formValues, setFormValues] = useState({});

    // ── WORK-RIGHTS GATE (client request, 30 Jul 2026): not an AU/NZ citizen,
    //    not a permanent resident AND no work visa = no legal right to work
    //    for Armourguard. A modal explains why, the rest of the Work Auth step
    //    greys out and Next is disabled until the answers change.
    const [workRightsModalOpen, setWorkRightsModalOpen] = useState(false);
    const workRightsWarned = useRef(false);
    const noWorkRights = formValues?.wa_nzCitizen === 'No'
        && formValues?.wa_permanentResident === 'No'
        && formValues?.wa_visaAuthority === 'No';
    useEffect(() => {
        if (noWorkRights && !workRightsWarned.current) {
            workRightsWarned.current = true;
            setWorkRightsModalOpen(true);
        }
        if (!noWorkRights) workRightsWarned.current = false;   // re-arm if answers change
    }, [noWorkRights]);

    // Auth state
    const [candidateInfo, setCandidateInfo] = useState(null);
    // Signed-in EMPLOYEE applying for an internal role (gated-apply build,
    // 31 Aug 2026). Not a candidate session: no drafts, no My Applications,
    // no candidate notification bell - but the application form is unlocked.
    const [staffInfo, setStaffInfo] = useState(null);
    const [authModalVisible, setAuthModalVisible] = useState(false);
    const [authModalMode, setAuthModalMode] = useState("login");
    const [activeView, setActiveView] = useState("jobs"); // "jobs" | "internal" | "dashboard"
    const [draftSaving, setDraftSaving] = useState(false);
    const [hasDraft, setHasDraft] = useState(false);
    const [draftFiles, setDraftFiles] = useState({}); // { cv: {fileName, storedPath, fileSize}, photo: {...}, ... }
    const [helpOpen, setHelpOpen] = useState(false);

    // ── WIZARD SCROLL-TO-TOP (client request, 27 Aug 2026) ──────────────
    // Next/Previous sit at the bottom of every step, so after a click the
    // candidate landed at the bottom of the NEW step and had to scroll up.
    // goToStep() changes the step and then scrolls the wizard card (Steps
    // bar + first field) to just under the sticky TopNav. We target the
    // wizard card rather than the top of the window so a long job
    // description above the form is not re-shown on every step.
    const wizardRef = useRef(null);
    // The sticky TopNav is one row (~54px) on desktop but wraps to two rows on
    // phones, so its real height is measured at scroll time.
    const navRef = useRef(null);
    const WIZARD_SCROLL_GAP = 18; // breathing room below the sticky TopNav
    function goToStep(step) {
        setCurrentStep(step);
        // Let React paint the new step first, then scroll.
        requestAnimationFrame(() => {
            const el = wizardRef.current;
            if (el && typeof el.scrollIntoView === 'function') {
                // scrollIntoView works whether the page scrolls the window or a
                // nested overflow container (Razor layout wrappers). The wrapper
                // div gets scrollMarginTop = nav height + gap so it lands below the sticky nav.
                el.style.scrollMarginTop = ((navRef.current?.offsetHeight || 54) + WIZARD_SCROLL_GAP) + 'px';
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        });
    }

    // ── SUBMIT SAFETY (01 Sep 2026, post-preview-build fix) ─────────────
    // Next and Submit render in the same NAV slot, so React reuses the same
    // DOM <button> node. Clicking Next on the last form step flipped that
    // node into a submit button while the browser was still processing the
    // click, and its default action submitted the form — the application
    // went off without the preview ever being seen. Defence in depth:
    //   (1) the two NAV branches now carry distinct keys, so the node is
    //       remounted rather than morphed;
    //   (2) Submit is htmlType="button" and calls form.submit() itself, so
    //       NO native submit button exists anywhere — no click default
    //       action or stray Enter key can ever submit the form;
    //   (3) Submit stays disarmed (disabled) for a moment after the review
    //       step appears, so a double-click on Next can't fire it either.
    // Effect (not goToStep) so a draft resumed straight onto step 7 arms too.
    const [submitArmed, setSubmitArmed] = useState(false);
    useEffect(() => {
        if (currentStep !== 7) { setSubmitArmed(false); return; }
        const t = setTimeout(() => setSubmitArmed(true), 900);
        return () => clearTimeout(t);
    }, [currentStep]);

    // Immediately upload a file to draft storage on server
    async function uploadDraftFile(fileKey, file) {
        if (!candidateInfo) return null;
        try {
            const fd = new FormData();
            fd.append("fileKey", fileKey);
            fd.append("file", file);
            const res = await fetch("/api/public/draft-upload", { method: "POST", body: fd });
            if (!res.ok) { const err = await res.json(); message.error(err.error || "Upload failed"); return null; }
            const data = await res.json();
            setDraftFiles(prev => ({ ...prev, [fileKey]: { fileName: data.fileName, storedPath: data.storedPath, fileSize: data.fileSize } }));
            return data;
        } catch { message.error("File upload failed"); return null; }
    }

    // Check session on load
    useEffect(() => {
        checkSession();
        loadVacancies();
        // Session policy (Nov 2026): back here after an idle timeout — say so and offer the sign-in.
        if (/[?&]reason=expired/.test(location.search)) {
            try { history.replaceState(null, "", location.pathname); } catch { /* ignore */ }
            message.warning("Your session expired, please sign in again.", 6);
            openAuth("login");
        }
    }, []);

    // Session policy (Nov 2026): a signed-in candidate or staff member is timed out after 30 idle minutes
    // (wwwroot/js/session-idle.js, loaded by the careers page). The timer only runs while someone is signed in.
    function startIdleTimer() {
        const si = window.SessionIdle;
        if (!si) return;
        const o = window.__sessionIdle || {};
        si.start({
            idleMinutes: o.idleMinutes, warnMinutes: o.warnMinutes,
            ping: "/api/public/candidate-me",
            logout: "/api/public/candidate-logout", logoutMethod: "POST",
            redirect: () => location.pathname + "?reason=expired"
        });
    }
    function stopIdleTimer() { if (window.SessionIdle) window.SessionIdle.stop(); }

    async function checkSession() {
        try {
            const res = await fetchJson("/api/public/candidate-me");
            if (res.loggedIn) {
                setCandidateInfo(res);
                setStaffInfo(null);
                startIdleTimer();
                return res;
            }
            if (res.staffLoggedIn) {
                setStaffInfo(res);
                setCandidateInfo(null);
                startIdleTimer();
                return res;
            }
            setCandidateInfo(null);
            setStaffInfo(null);
            stopIdleTimer();
        } catch { /* not logged in */ }
        return null;
    }

    // Applying is gated behind an account, so signing in from a job page has to
    // drop the applicant straight into the form - unlocked, pre-filled and with
    // any saved draft restored - rather than making them re-open the vacancy.
    async function handleAuthSuccess() {
        const session = await checkSession();
        if (!session || !selected) return;

        const draftLoaded = session.loggedIn
            ? await loadDraft(selected.id, selected, session)
            : false;

        if (!draftLoaded) {
            const prefill = {
                firstName: session.firstName || '',
                lastName: session.lastName || '',
                email: session.email || '',
                phone: session.phone || ''
            };
            form.setFieldsValue(prefill);
            setFormValues(prefill);
        }
    }

    async function handleLogout() {
        try {
            await fetchJson("/api/public/candidate-logout", { method: "POST" });
            setCandidateInfo(null);
            setStaffInfo(null);
            stopIdleTimer();
            setActiveView("jobs");
            message.success("Logged out successfully");
        } catch { message.error("Logout failed"); }
    }

    function openAuth(mode) {
        setAuthModalMode(mode);
        setAuthModalVisible(true);
    }

    // ---- DRAFT FUNCTIONS ----
    async function saveDraft() {
        if (!candidateInfo || !selected) {
            message.warning("Please sign in to save a draft");
            openAuth("login");
            return;
        }
        setDraftSaving(true);
        try {
            const allVals = form.getFieldsValue(true);
            const serializable = { ...allVals };

            // Remove screening_* fields from form values — they contain dayjs/objects
            // that don't serialize safely. We store them separately as strings in _screeningAnswers.
            Object.keys(serializable).forEach(k => {
                if (k.startsWith("screening_")) delete serializable[k];
            });

            serializable._screeningAnswers = screeningAnswers;
            serializable._draftFiles = draftFiles;

            await fetchJson("/api/public/save-draft", {
                method: "POST",
                body: JSON.stringify({
                    vacancyId: selected.id,
                    formDataJson: JSON.stringify(serializable),
                    lastStep: currentStep,
                    uploadedFilesJson: JSON.stringify(draftFiles)
                })
            });
            setHasDraft(true);
            message.success("Draft saved! You can continue later from 'My Applications'.");
        } catch (err) {
            message.error(err.error || "Failed to save draft");
        } finally { setDraftSaving(false); }
    }

    async function loadDraft(vacancyId, vacancyDetail, sessionOverride = null) {
        // sessionOverride: the session just returned by checkSession(). Needed when
        // the applicant signs in mid-page, before candidateInfo has re-rendered.
        if (!candidateInfo && !sessionOverride) return false;
        try {
            const res = await fetchJson(`/api/public/draft/${vacancyId}`);
            if (res.hasDraft) {
                const data = JSON.parse(res.formDataJson || "{}");
                const screeningData = data._screeningAnswers || {};
                const savedDraftFiles = data._draftFiles || {};
                delete data._screeningAnswers;
                delete data._draftFiles;

                // Remove any stale screening_* fields that may have leaked into saved data
                Object.keys(data).forEach(k => {
                    if (k.startsWith("screening_")) delete data[k];
                });

                // Restore draft file metadata
                const restoredFilesByQ = {};
                Object.keys(savedDraftFiles).forEach(key => {
                    const info = savedDraftFiles[key];
                    if (info && info.storedPath) {
                        restoredFilesByQ[key] = {
                            uid: `draft-${key}`,
                            name: info.fileName || `${key} (saved)`,
                            status: 'done',
                            isDraft: true,
                            storedPath: info.storedPath,
                            url: `/api/public/draft-file/${info.storedPath}`
                        };
                    }
                });

                setTimeout(() => {
                    // Set non-screening form values first
                    form.setFieldsValue(data);
                    setFormValues(data);
                    setScreeningAnswers(screeningData);

                    // Restore screening answers onto form fields with type-safe conversion
                    // We need the loaded vacancy's screening questions for type checking
                    Object.keys(screeningData).forEach(k => {
                        const val = screeningData[k];
                        if (val === undefined || val === null || val === '') return;

                        const qId = k.replace("screening_", "");
                        // Find the question to check its type
                        const q = vacancyDetail?.screeningQuestions?.find(sq => sq.id === qId);
                        const qType = normalizeQuestionType(q?.questionType);

                        if (qType === "date") {
                            // Convert string back to dayjs for DatePicker fields
                            const dVal = dayjs(val);
                            form.setFieldValue(k, dVal.isValid() ? dVal : null);
                        } else {
                            form.setFieldValue(k, val);
                        }
                    });

                    setDraftFiles(savedDraftFiles);
                    setFilesByQ(restoredFilesByQ);
                    setCurrentStep(Math.min(res.lastStep || 0, 7));
                    setHasDraft(true);
                }, 150);
                return true;
            }
            return false;
        } catch (err) {
            console.error("[DRAFT] Load error:", err);
            return false;
        }
    }

    async function deleteDraft(draftId) {
        try {
            await fetchJson(`/api/public/draft/${draftId}`, { method: "DELETE" });
        } catch { /* non-fatal */ }
    }

    async function loadVacancies() {
        setLoading(true);
        try {
            const rows = await fetchJson("/api/public/vacancies");
            setVacancies(rows || []);
        } catch (err) {
            message.error(err.error || err.message || "Failed to load vacancies");
        } finally { setLoading(false); }
    }

    async function viewDetails(slug) {
        setLoading(true);
        try {
            const detail = await fetchJson(`/api/public/vacancies/${encodeURIComponent(slug)}`);
            setSelected(detail);
            setCurrentStep(0);
            // Same class of issue: "View & Apply" was clicked part-way down the
            // jobs grid, so the detail page opened at that scroll offset.
            window.scrollTo({ top: 0 });
            form.resetFields();
            setFilesByQ({});
            setScreeningAnswers({});
            setFormValues({});
            setSubmissionSuccess(null);
            setDuplicateApplication(null);
            setHasDraft(false);

            // Try loading a saved draft first, then fall back to profile auto-fill.
            // Drafts are candidate-only; a staff (internal) applicant just gets
            // their details pre-filled.
            const profile = candidateInfo || staffInfo;
            if (profile) {
                const draftLoaded = candidateInfo ? await loadDraft(detail.id, detail) : false;
                if (!draftLoaded) {
                    setTimeout(() => {
                        const prefill = {
                            firstName: profile.firstName || '',
                            lastName: profile.lastName || '',
                            email: profile.email || '',
                            phone: profile.phone || ''
                        };
                        form.setFieldsValue(prefill);
                        setFormValues(prefill);
                    }, 100);
                }
            }
        } catch (err) {
            message.error(err.error || err.message || "Failed to load job details");
        } finally { setLoading(false); }
    }

    const visibleQuestions = useMemo(() => {
        if (!selected?.screeningQuestions) return [];
        return selected.screeningQuestions.filter(q => {
            if (!q.parentQuestionId) return true;
            const parent = selected.screeningQuestions.find(p => p.id === q.parentQuestionId);
            if (!parent) return true;
            const parentAnswer = screeningAnswers[`screening_${parent.id}`];
            if (!parentAnswer) return false;
            const answerStr = Array.isArray(parentAnswer) ? parentAnswer.join(', ') : String(parentAnswer);
            return answerStr === q.parentAnswerCondition;
        });
    }, [selected?.screeningQuestions, screeningAnswers]);

    function isQuestionAnswered(questionId) {
        const answer = screeningAnswers[`screening_${questionId}`];
        return answer !== undefined && answer !== null && answer !== '' && !(Array.isArray(answer) && answer.length === 0);
    }

    // Task Ability Q5 / Q6: both "No" blocks Next on the health step and the final submit (server checks too).
    const bothMedicalNo = formValues?.health_preEmploymentMedical === 'No' && formValues?.health_ongoingMedical === 'No';
    const stepValidation = useMemo(() => {
        const step1Complete = !!(formValues.firstName && formValues.lastName && formValues.email);
        const step2Complete = !!filesByQ.cv;
        // Verification (re-hire) — No is complete; Yes needs ALL fields.
        // 02 Aug 2026: previous workplace is FREE TEXT — routing uses the
        // reporting manager of the role applied for, so no entity/location
        // dropdowns are needed. All fields stay compulsory on Yes.
        const verificationComplete = formValues.rehire_isPreviousStaff === 'No'
            || (formValues.rehire_isPreviousStaff === 'Yes' && !!formValues.rehire_previousLocation
                && !!formValues.rehire_previousRole && !!formValues.rehire_approxPeriod && !!formValues.rehire_notes);
        const requiredQuestions = visibleQuestions.filter(q => q.isRequired);
        const answeredRequired = requiredQuestions.filter(q => isQuestionAnswered(q.id));
        const step3Complete = requiredQuestions.length === 0 || answeredRequired.length === requiredQuestions.length;
        const step3Progress = requiredQuestions.length > 0 ? Math.round((answeredRequired.length / requiredQuestions.length) * 100) : 100;
        return {
            step1Complete, step2Complete, verificationComplete, step3Complete, step3Progress,
            requiredTotal: requiredQuestions.length,
            requiredAnswered: answeredRequired.length,
            medicalConsentOk: !bothMedicalNo,
            canSubmit: step1Complete && step2Complete && verificationComplete && step3Complete && !bothMedicalNo
        };
    }, [formValues, filesByQ, visibleQuestions, screeningAnswers, bothMedicalNo]);

    function handleScreeningAnswerChange(questionId, value) {
        let stringValue = value;
        if (value && typeof value === 'object' && value.$d) {
            stringValue = value.format('YYYY-MM-DD');
        } else if (Array.isArray(value)) {
            stringValue = value.length > 0 ? value.join(', ') : '';
        } else if (value !== null && value !== undefined) {
            stringValue = String(value);
        }
        setScreeningAnswers(prev => ({ ...prev, [`screening_${questionId}`]: stringValue }));
    }

    function handleFormValuesChange(changedValues, allValues) {
        setFormValues(allValues);
    }

    // SUBMIT FIX (Oct 2026): never fail silently. Name the step and the field,
    // jump to that step and scroll the field into view (antd marks it red).
    function fieldLabel(name) {
        const q = String(name).startsWith('screening_') ? selected?.screeningQuestions?.find(x => `screening_${x.id}` === name) : null;
        const item = q ? null : document.getElementById(name)?.closest('.ant-form-item');
        const txt = q ? q.questionText
            : (item?.querySelector('.ant-form-item-label label')?.textContent
                || item?.closest('.careers-qrow')?.querySelector('.careers-qrow-label')?.textContent
                || item?.querySelector('.ant-checkbox-wrapper')?.textContent);
        const s = (txt || String(name)).replace(/\s+/g, ' ').replace(/^\*\s*/, '').trim();
        return s.length > 90 ? s.slice(0, 87) + '…' : s;
    }
    function handleSubmitFailed({ errorFields }) {
        const first = errorFields?.[0];
        if (!first) return;
        const name = Array.isArray(first.name) ? first.name[0] : first.name;
        const step = stepForField(name);
        message.error({
            content: `Step ${step + 1} (${STEP_TITLES[step]}) — "${fieldLabel(name)}": ${first.errors?.[0] || 'needs attention'}. Please complete it, then return to Review & Submit.`,
            duration: 8
        });
        goToStep(step);
        // Let the step render (goToStep scrolls the wizard first), then bring the field itself into view.
        setTimeout(() => {
            try { form.scrollToField(name, { block: 'center', behavior: 'smooth' }); } catch { /* not in DOM */ }
            const el = document.getElementById(name);
            if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
        }, 450);
    }

    // One in-flight submit at a time (sync ref — state alone lags a click behind).
    const submitLock = useRef(false);
    async function handleSubmit(values) {
        // Hard guard (01 Sep 2026): whatever triggers a submit, nothing goes
        // to the server unless the applicant is on the Review & Submit step.
        if (currentStep !== 7) return;
        if (bothMedicalNo) { message.error(MEDICAL_CONSENT_MSG); goToStep(5); return; }
        if (!stepValidation.canSubmit) { message.error("Please complete all required sections"); return; }
        if (submitLock.current) return;
        submitLock.current = true;
        setApplying(true);
        try {
            const fd = new FormData();
            fd.append("firstName", values.firstName);
            fd.append("lastName", values.lastName);
            fd.append("email", values.email);
            fd.append("phone", values.phone || "");
            fd.append("coverLetter", values.coverLetter || "");

            // Append fresh files only (File objects, not draft placeholders)
            if (filesByQ.cv && filesByQ.cv instanceof File) fd.append("cv", filesByQ.cv);
            if (filesByQ.coverLetterFile && filesByQ.coverLetterFile instanceof File) fd.append("coverLetterFile", filesByQ.coverLetterFile);

            // New personal info fields
            fd.append("ageRange", values.ageRange || "");
            fd.append("gender", values.gender || "");
            fd.append("ethnicity", values.ethnicity || "");
            fd.append("ethnicityOther", values.ethnicityOther || "");
            fd.append("employmentStatus", values.employmentStatus || "");
            fd.append("identificationType", values.identificationType || "");
            if (filesByQ.photo && filesByQ.photo instanceof File) fd.append("photo", filesByQ.photo);
            if (filesByQ.idDocument && filesByQ.idDocument instanceof File) fd.append("idDocument", filesByQ.idDocument);

            // Send draft file metadata so backend can use stored draft files as fallback
            if (Object.keys(draftFiles).length > 0) {
                fd.append("draftFilesJson", JSON.stringify(draftFiles));
            }

            // Re-hire verification (Verification step). All previous-service
            // details travel as one JSON blob — approval routing is resolved
            // server-side from the vacancy's location (reporting manager).
            fd.append("isPreviousStaff", values.rehire_isPreviousStaff || "No");
            if (values.rehire_isPreviousStaff === "Yes") {
                const verificationObj = {};
                if (values.rehire_previousLocation) verificationObj.previousLocation = values.rehire_previousLocation;
                if (values.rehire_approxPeriod) verificationObj.approxPeriod = values.rehire_approxPeriod;
                if (values.rehire_previousRole) verificationObj.previousRole = values.rehire_previousRole;
                if (values.rehire_notes) verificationObj.notes = values.rehire_notes;
                if (Object.keys(verificationObj).length > 0) {
                    fd.append("verificationJson", JSON.stringify(verificationObj));
                }
            }

            // Screening answers
            const answersArr = [];
            for (const key of Object.keys(screeningAnswers)) {
                const qId = key.replace("screening_", "");
                const ansVal = screeningAnswers[key];
                if (qId && ansVal !== undefined && ansVal !== null && ansVal !== '') {
                    answersArr.push({ questionId: qId, answerText: String(ansVal) });
                }
            }
            fd.append("answersJson", JSON.stringify(answersArr));

            // Health / Task Ability questionnaire (strip the "health_" prefix to
            // match the onboarding "healthQuestionnaire" section keys exactly).
            const healthObj = {};
            Object.keys(values || {}).forEach((k) => {
                if (k.startsWith("health_") && k !== "health_declaration") {
                    const v = values[k];
                    if (v !== undefined && v !== null && v !== "") healthObj[k.slice(7)] = v;
                }
            });
            if (Object.keys(healthObj).length > 0) {
                fd.append("healthQuestionnaireJson", JSON.stringify(healthObj));
            }

            // Criminal-history declaration (strip the "crim_" prefix to match the
            // onboarding "criminalHistory" section keys exactly — 1:1 seeding).
            const crimObj = {};
            Object.keys(values || {}).forEach((k) => {
                if (k.startsWith("crim_") && k !== "crim_declaration") {
                    const v = values[k];
                    if (v !== undefined && v !== null && v !== "") crimObj[k.slice(5)] = v;
                }
            });
            if (Object.keys(crimObj).length > 0) {
                fd.append("criminalHistoryJson", JSON.stringify(crimObj));
            }

            // Work Auth & Licence (strip the "wa_" prefix — 1:1 with the
            // onboarding "workAuth" section for seeding).
            const waObj = {};
            Object.keys(values || {}).forEach((k) => {
                if (k.startsWith("wa_")) {
                    const v = values[k];
                    if (v !== undefined && v !== null && v !== "") waObj[k.slice(3)] = v;
                }
            });
            if (Object.keys(waObj).length > 0) {
                fd.append("workAuthJson", JSON.stringify(waObj));
            }

            // Employment History & References (strip "eh_" — 1:1 with the
            // onboarding "employmentHistory" section for seeding).
            const ehObj = {};
            Object.keys(values || {}).forEach((k) => {
                if (k.startsWith("eh_")) {
                    const v = values[k];
                    if (v !== undefined && v !== null && v !== "") ehObj[k.slice(3)] = v;
                }
            });
            if (Object.keys(ehObj).length > 0) {
                fd.append("employmentHistoryJson", JSON.stringify(ehObj));
            }

            const res = await fetch(`/api/public/vacancies/${encodeURIComponent(selected.publicSlug || selected.id)}/apply`, { method: "POST", body: fd });
            const data = await res.json();

            if (!res.ok) {
                if (data.error === "duplicate_application") {
                    setDuplicateApplication({ applicationNumber: data.applicationNumber, appliedDate: data.appliedDate });
                } else if (res.status === 401 || data.error === "auth_required") {
                    // Session expired mid-application - re-open sign-in rather than
                    // dropping everything they have typed.
                    message.warning(data.message || "Please sign in again to submit your application.");
                    setCandidateInfo(null);
                    setStaffInfo(null);
                    openAuth("login");
                } else {
                    message.error(data.error || data.message || "Submission failed");
                }
            } else {
                setSubmissionSuccess({
                    applicationNumber: data.applicationNumber,
                    appliedDate: data.appliedDate,
                    jobTitle: selected.title,
                    email: values.email
                });
            }
        } catch (err) { message.error("Submission failed. Please try again."); }
        finally { submitLock.current = false; setApplying(false); }
    }

    function renderRequiredTag(questionId, isRequired) {
        if (!isRequired) return <Tag>Optional</Tag>;
        return isQuestionAnswered(questionId)
            ? <Tag color="success" icon={<CheckCircleOutlined />}>Answered</Tag>
            : <Tag color="error">Required</Tag>;
    }

    // Normalize questionType from either integer (backend enum) or string (ToString) to a consistent lowercase key
    function normalizeQuestionType(qt) {
        const intMap = { 0: 'text', 1: 'yesno', 2: 'singlechoice', 3: 'multichoice', 4: 'date', 5: 'number', 6: 'file' };
        if (qt === null || qt === undefined || qt === '') return 'text';
        if (typeof qt === 'number' || /^\d+$/.test(qt)) return intMap[Number(qt)] || 'text';
        return String(qt).toLowerCase().replace(/\s+/g, '');
    }

    // Notice period the applicant stated on a screening question (e.g. "4 weeks",
    // "2 Weeks", "1 month") → weeks. 0 when none / not parseable.
    function noticeWeeksFromAnswers() {
        const qs = selected?.screeningQuestions || [];
        const nq = qs.find(x => /notice/i.test(x.questionText || ''));
        if (!nq) return 0;
        const raw = String(screeningAnswers[`screening_${nq.id}`] ?? '').toLowerCase();
        const m = /(\d+(?:\.\d+)?)\s*(week|wk|month|mth|day)/.exec(raw);
        if (!m) return 0;
        const n = parseFloat(m[1]);
        if (/month|mth/.test(m[2])) return Math.round(n * 4.33);
        if (/day/.test(m[2])) return Math.ceil(n / 7);
        return Math.round(n);
    }
    function earliestStartDate() {
        const weeks = noticeWeeksFromAnswers();
        return dayjs().startOf('day').add(weeks, 'week');
    }
    // Minimum date for an "earliest available start" screening question; null for other dates.
    function startDateMin(q) {
        return /start|available|commence|begin/i.test(q.questionText || '') ? earliestStartDate() : null;
    }
    // Hint shown under a start-date question — rendered as a sibling of the
    // Form.Item, never inside it (see the 'date' case in renderQuestionInput).
    function renderQuestionHint(q) {
        if (normalizeQuestionType(q.questionType) !== 'date') return null;
        const minDate = startDateMin(q);
        if (!minDate) return null;
        return (
            <div style={{ fontSize: 12, color: '#64748b', marginTop: -18, marginBottom: 12 }}>
                Earliest selectable date: {minDate.format('DD/MM/YYYY')}{noticeWeeksFromAnswers() > 0 ? ` (today plus your ${noticeWeeksFromAnswers()}-week notice period)` : ' (dates in the past cannot be selected)'}
            </div>
        );
    }

    function renderQuestionInput(q) {
        const onChange = (val) => handleScreeningAnswerChange(q.id, val);
        const qType = normalizeQuestionType(q.questionType);
        let options = [];
        try { if (q.optionsJson) options = JSON.parse(q.optionsJson); } catch { }

        switch (qType) {
            case 'yesno':
                return <Radio.Group onChange={(e) => onChange(e.target.value)} options={[{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]} />;
            case 'singlechoice':
            case 'dropdown':
            case 'select':
                return <Select placeholder="Select..." onChange={onChange} allowClear style={{ width: '100%' }} getPopupContainer={trigger => trigger.parentNode}>
                    {options.map((o, i) => <Select.Option key={i} value={typeof o === 'string' ? o : o.value || o.label}>{typeof o === 'string' ? o : o.label || o.value}</Select.Option>)}
                </Select>;
            case 'multichoice':
            case 'multiselect':
            case 'multi-select':
            case 'checkbox':
                return <Checkbox.Group onChange={onChange} options={options.map(o => typeof o === 'string' ? o : o.value || o.label)} />;
            case 'number':
            case 'numeric':
                return <InputNumber style={{ width: '100%' }} onChange={onChange} />;
            case 'date': {
                // Client UAT (Oct 2026): an "earliest available start" date could be in the
                // past, and ignored the notice period the applicant had just given.
                // SUBMIT FIX (Oct 2026): the DatePicker must be the ONLY child of its
                // Form.Item. antd binds value/onChange/id to a single child; wrapped in a
                // fragment with the hint it was never connected to the form, so the
                // required rule failed on submit while the review said "complete". The
                // hint now renders beside the Form.Item (renderQuestionHint).
                const minDate = startDateMin(q);
                return <DatePicker style={{ width: '100%' }} onChange={onChange} format="DD/MM/YYYY"
                    disabledDate={minDate ? (d) => d && d.isBefore(minDate, 'day') : undefined}
                    getPopupContainer={trigger => trigger.parentNode} />;
            }
            case 'textarea':
            case 'longtext':
                return <Input.TextArea rows={4} onChange={(e) => onChange(e.target.value)} maxLength={2000} showCount />;
            case 'fileupload':
            case 'file':
                return (
                    <Upload beforeUpload={(file) => { setFilesByQ(prev => ({ ...prev, [q.id]: file })); onChange(file.name); return false; }}
                        onRemove={() => { setFilesByQ(prev => { const u = { ...prev }; delete u[q.id]; return u; }); onChange(''); }}
                        maxCount={1} accept=".pdf,.doc,.docx,.jpg,.png">
                        <Button icon={<UploadOutlined />}>Upload File</Button>
                    </Upload>
                );
            default:
                // Smart fallback: if options exist but type was saved incorrectly, render as dropdown
                if (options.length > 0) {
                    return <Select placeholder="Select..." onChange={onChange} allowClear style={{ width: '100%' }} getPopupContainer={trigger => trigger.parentNode}>
                        {options.map((o, i) => <Select.Option key={i} value={typeof o === 'string' ? o : o.value || o.label}>{typeof o === 'string' ? o : o.label || o.value}</Select.Option>)}
                    </Select>;
                }
                return <Input onChange={(e) => onChange(e.target.value)} />;
        }
    }

    // Internal = advertised "Internal" or "Internal & External".
    // Public (Jobs tab) = "External", "Internal & External", or unspecified (manually created).
    const isInternalJob = (v) => {
        const p = (v.advertisingPlan || "").trim().toLowerCase();
        return p === "internal" || p === "internal & external";
    };
    const isPublicJob = (v) => {
        const p = (v.advertisingPlan || "").trim().toLowerCase();
        return p === "" || p === "external" || p === "internal & external";
    };
    // Applying requires an account (31 Aug 2026): either a verified candidate
    // login, or a signed-in employee applying for an internal role.
    const isAuthed = !!candidateInfo || !!staffInfo;

    // Internal view is staff-only (client UAT, Oct 2026): anyone else sees the public list.
    const internalView = activeView === "internal" && !!staffInfo;

    const internalCount = vacancies.filter(isInternalJob).length;
    const publicCount = vacancies.filter(isPublicJob).length;

    const filtered = vacancies.filter((v) => {
        const matchesSearch = !searchText || v.title?.toLowerCase().includes(searchText.toLowerCase()) ||
            v.divisionName?.toLowerCase().includes(searchText.toLowerCase()) || v.locationName?.toLowerCase().includes(searchText.toLowerCase());
        const matchesLocation = locationFilter === "all" || v.locationName === locationFilter;
        const matchesView = internalView ? isInternalJob(v) : isPublicJob(v);
        return matchesSearch && matchesLocation && matchesView;
    });

    const uniqueLocations = [...new Set(vacancies.filter(internalView ? isInternalJob : isPublicJob).map((v) => v.locationName).filter(Boolean))];

    // =================== TOP NAV BAR ====================
    const TopNav = () => (
        <div style={{
            background: '#fff', borderBottom: '1px solid #e8e8e8',
            padding: '8px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            position: 'sticky', top: 0, zIndex: 1000, minHeight: 48
        }} className="careers-topnav" ref={navRef}>
            <Space size="middle">
                <img src="/images/logo.png" alt="Armourguard" style={{ height: 36, cursor: 'pointer' }}
                    onClick={() => { setSelected(null); setActiveView("jobs"); setSubmissionSuccess(null); setDuplicateApplication(null); }} />
                <Button type={activeView === "jobs" ? "primary" : "text"} size="small"
                    icon={<SearchOutlined />}
                    onClick={() => { setSelected(null); setActiveView("jobs"); setSubmissionSuccess(null); setDuplicateApplication(null); }}
                    style={activeView === "jobs" ? { background: '#1e3a5f', borderColor: '#1e3a5f' } : {}}>
                    Jobs
                </Button>
                {/* Internal roles are for signed-in Armourguard staff only (client UAT, Oct 2026) */}
                {staffInfo && (
                <Button type={activeView === "internal" ? "primary" : "text"} size="small"
                    icon={<LockOutlined />}
                    onClick={() => { setSelected(null); setActiveView("internal"); setSubmissionSuccess(null); setDuplicateApplication(null); }}
                    style={activeView === "internal" ? { background: '#1e3a5f', borderColor: '#1e3a5f' } : {}}>
                    Internal Jobs
                </Button>
                )}
                {candidateInfo && (
                    <Button type={activeView === "dashboard" ? "primary" : "text"} size="small"
                        icon={<DashboardOutlined />}
                        onClick={() => { setSelected(null); setActiveView("dashboard"); setSubmissionSuccess(null); setDuplicateApplication(null); }}
                        style={activeView === "dashboard" ? { background: '#1e3a5f', borderColor: '#1e3a5f' } : {}}>
                        My Applications
                    </Button>
                )}
            </Space>

            <Space size={12} style={{ flexShrink: 0 }} className="careers-topnav-right">
                <Button onClick={() => setHelpOpen(true)}
                    style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)', border: 'none', color: 'white', fontWeight: 600, borderRadius: 8, boxShadow: '0 2px 8px rgba(124,58,237,0.3)' }}
                    icon={<QuestionCircleOutlined />}>Help</Button>
                {candidateInfo?.hasOnboarding && (
                    <Button
                        type="primary"
                        icon={<IdcardOutlined />}
                        style={{ background: '#16A34A', borderColor: '#16A34A' }}
                        onClick={() => { window.location.href = APP_BASE + '/my-onboarding'; }}>
                        My Onboarding
                    </Button>
                )}
                {candidateInfo && <NotificationBell />}
                {candidateInfo ? (
                    <Dropdown menu={{
                        items: [
                            { key: 'dash', icon: <DashboardOutlined />, label: 'My Applications', onClick: () => { setSelected(null); setActiveView("dashboard"); } },
                            { type: 'divider' },
                            { key: 'logout', icon: <LogoutOutlined />, label: 'Sign Out', danger: true, onClick: handleLogout }
                        ]
                    }}>
                        <Button type="text" style={{ padding: '4px 12px' }}>
                            <Space>
                                <Avatar size={28} style={{ backgroundColor: '#1e3a5f' }}>
                                    {candidateInfo.firstName?.[0] || 'C'}
                                </Avatar>
                                <span style={{ fontWeight: 500 }}>{candidateInfo.displayName}</span>
                            </Space>
                        </Button>
                    </Dropdown>
                ) : staffInfo ? (
                    /* Signed-in employee browsing internal roles - they already hold
                       an HRIS session, so no careers sign-in / register prompt. */
                    <Space size={8}>
                        <Avatar size={28} style={{ backgroundColor: '#0f766e' }}>
                            {(staffInfo.firstName || staffInfo.displayName || 'S')[0]}
                        </Avatar>
                        <span style={{ fontWeight: 500 }} className="careers-topnav-name" title={staffInfo.displayName}>{staffInfo.displayName}</span>
                        <Tag color="blue">Staff</Tag>
                    </Space>
                ) : (
                    <>
                        <Button type="text" onClick={() => openAuth("login")} icon={<LoginOutlined />}>Sign In</Button>
                        <Button type="primary" onClick={() => openAuth("register")}
                            style={{ background: '#1e3a5f', borderColor: '#1e3a5f' }}>
                            Register
                        </Button>
                    </>
                )}
            </Space>
        </div>
    );

    // =================== LOADING STATE ====================
    if (loading && !selected && vacancies.length === 0) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><Spin size="large" tip="Loading..." /></div>;
    }

    // =================== DUPLICATE APPLICATION ====================
    if (duplicateApplication) {
        return (
            <App>
                <TopNav />
                <div className="careers-container">
                    <div style={{ maxWidth: 600, margin: '60px auto', padding: '0 20px' }}>
                        <Card>
                            <Result status="warning" icon={<WarningOutlined style={{ color: '#faad14' }} />}
                                title="You Have Already Applied"
                                subTitle={
                                    <div style={{ textAlign: 'left', marginTop: 16 }}>
                                        <p>You have already submitted an application for <strong>{selected?.title}</strong>.</p>
                                        <div style={{ background: '#fffbe6', padding: 20, borderRadius: 8, marginTop: 16, border: '1px solid #ffe58f' }}>
                                            <p style={{ margin: 0, fontSize: 14, color: '#8c8c8c' }}>Your Existing Application:</p>
                                            <p style={{ margin: '8px 0 0 0', fontSize: 28, fontWeight: 700, color: '#d48806' }}>{duplicateApplication.applicationNumber}</p>
                                            <Divider style={{ margin: '16px 0' }} />
                                            <Row><Col span={8}><Text type="secondary">Submitted On:</Text></Col><Col span={16}><Text strong>{duplicateApplication.appliedDate}</Text></Col></Row>
                                        </div>
                                        <Alert message="Each candidate can have one active application per position. If you withdraw it from My Applications, you can apply again." type="info" showIcon style={{ marginTop: 20 }} />
                                    </div>
                                }
                                extra={[
                                    <Button type="primary" key="browse" size="large" onClick={() => { setDuplicateApplication(null); setSelected(null); }}>Browse Other Jobs</Button>,
                                    <Button key="back" size="large" onClick={() => setDuplicateApplication(null)}>Back to Application</Button>
                                ]}
                            />
                        </Card>
                    </div>
                </div>
                <AuthModal visible={authModalVisible} onClose={() => setAuthModalVisible(false)} onAuthSuccess={handleAuthSuccess} initialMode={authModalMode} />
                <CareersHelpGuide open={helpOpen} onClose={() => setHelpOpen(false)} />
            </App>
        );
    }

    // =================== SUBMISSION SUCCESS ====================
    if (submissionSuccess) {
        return (
            <App>
                <TopNav />
                <div className="careers-container">
                    <div style={{ maxWidth: 600, margin: '60px auto', padding: '0 20px' }}>
                        <Card>
                            <Result status="success" title="Application Submitted Successfully!"
                                subTitle={
                                    <div style={{ textAlign: 'left', marginTop: 16 }}>
                                        <p>Thank you for applying for <strong>{submissionSuccess.jobTitle}</strong>.</p>
                                        <div style={{ background: '#f0f9ff', padding: 20, borderRadius: 8, marginTop: 16, border: '1px solid #bae6fd' }}>
                                            <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Your Application Number:</p>
                                            <p style={{ margin: '8px 0 0 0', fontSize: 28, fontWeight: 700, color: '#0369a1' }}>{submissionSuccess.applicationNumber}</p>
                                            <Divider style={{ margin: '16px 0' }} />
                                            <Row><Col span={8}><Text type="secondary">Submitted On:</Text></Col><Col span={16}><Text strong>{submissionSuccess.appliedDate}</Text></Col></Row>
                                        </div>
                                        <p style={{ marginTop: 16, color: '#64748b' }}>A confirmation email has been sent to <strong>{submissionSuccess.email}</strong>.</p>

                                        {/* Prompt to register if not logged in */}
                                        {!candidateInfo && !staffInfo && (
                                            <Alert
                                                message="Create an Account to Track Your Application"
                                                description="Register to view your application status, get updates, and apply faster next time."
                                                type="info" showIcon style={{ marginTop: 20 }}
                                                action={
                                                    <Button size="small" type="primary" onClick={() => openAuth("register")}
                                                        style={{ background: '#1e3a5f', borderColor: '#1e3a5f' }}>
                                                        Register Now
                                                    </Button>
                                                }
                                            />
                                        )}
                                    </div>
                                }
                                extra={[
                                    <Button type="primary" key="browse" size="large" onClick={() => { setSubmissionSuccess(null); setSelected(null); }}>Browse More Jobs</Button>,
                                    candidateInfo && (
                                        <Button key="dash" size="large" icon={<DashboardOutlined />}
                                            onClick={() => { setSubmissionSuccess(null); setSelected(null); setActiveView("dashboard"); }}>
                                            My Applications
                                        </Button>
                                    )
                                ].filter(Boolean)}
                            />
                        </Card>
                    </div>
                </div>
                <AuthModal visible={authModalVisible} onClose={() => setAuthModalVisible(false)} onAuthSuccess={handleAuthSuccess} initialMode={authModalMode} />
                <CareersHelpGuide open={helpOpen} onClose={() => setHelpOpen(false)} />
            </App>
        );
    }

    // =================== DASHBOARD VIEW ====================
    if (activeView === "dashboard" && candidateInfo) {
        return (
            <App>
                <TopNav />
                <div className="careers-container" style={{ paddingTop: 24, paddingBottom: 40 }}>
                    <MyApplicationsDashboard
                        candidateInfo={candidateInfo}
                        onViewJob={(slug) => { viewDetails(slug); setActiveView("jobs"); }}
                        onBrowseJobs={() => { setActiveView("jobs"); setSelected(null); }}
                    />
                </div>
                <AuthModal visible={authModalVisible} onClose={() => setAuthModalVisible(false)} onAuthSuccess={handleAuthSuccess} initialMode={authModalMode} />
                <CareersHelpGuide open={helpOpen} onClose={() => setHelpOpen(false)} />
            </App>
        );
    }

    // =================== JOB LISTING ====================
    if (!selected) {
        return (
            <App>
                <TopNav />
                <div className="careers-container">
                    <div className="hero-section">
                        <div className="hero-content">
                            <Title level={1} className="hero-title">{internalView ? "Internal Opportunities" : "Build Your Career With Us"}</Title>
                            <Paragraph className="hero-subtitle">{internalView ? "Roles open to current Armourguard team members" : "Join New Zealand's leading security services provider"}</Paragraph>
                            <Space size="large" className="hero-stats">
                                <div className="stat-item"><TeamOutlined className="stat-icon" /><div><div className="stat-number">500+</div><div className="stat-label">Team Members</div></div></div>
                                <div className="stat-item"><SafetyOutlined className="stat-icon" /><div><div className="stat-number">50+</div><div className="stat-label">Years of Excellence</div></div></div>
                                <div className="stat-item"><RocketOutlined className="stat-icon" /><div><div className="stat-number">{internalView ? internalCount : publicCount}</div><div className="stat-label">Open Positions</div></div></div>
                            </Space>
                        </div>
                    </div>
                    <div className="search-section">
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={16}>
                                <Input size="large" placeholder="Search jobs..." prefix={<SearchOutlined />} value={searchText} onChange={(e) => setSearchText(e.target.value)} allowClear />
                            </Col>
                            <Col xs={24} md={8}>
                                <Select size="large" style={{ width: '100%' }} value={locationFilter} onChange={setLocationFilter}>
                                    <Select.Option value="all">All Locations</Select.Option>
                                    {uniqueLocations.map((loc) => <Select.Option key={loc} value={loc}>{loc}</Select.Option>)}
                                </Select>
                            </Col>
                        </Row>
                    </div>
                    <div className="jobs-grid">
                        {filtered.length === 0 ? <Card><Empty description="No positions found" /></Card> : (
                            <Row gutter={[24, 24]}>
                                {filtered.map((v) => (
                                    <Col xs={24} sm={12} lg={8} key={v.id}>
                                        <Card hoverable className="job-card" onClick={() => viewDetails(v.publicSlug || v.id)}>
                                            <div className="job-card-content">
                                                <JobAdImage src={v.jobAdImageUrl} alt={v.title} />
                                                <div className="job-card-body">
                                                    <Title level={4} className="job-card-title">{v.title}</Title>
                                                    <Space direction="vertical" size="small">
                                                        {v.divisionName && <Tag color="blue">{v.divisionName}</Tag>}
                                                        {v.locationName && <Text type="secondary"><EnvironmentOutlined /> {v.locationName}</Text>}
                                                        {v.employmentType && <Text type="secondary"><ClockCircleOutlined /> {v.employmentType}</Text>}
                                                        {/* Headcount tracking (Oct 2026): always shown, singular when one position is open. */}
                                                        {v.positionsAvailable > 0 && <Text type="secondary"><TeamOutlined /> {v.positionsAvailable} {v.positionsAvailable === 1 ? 'position' : 'positions'} available</Text>}
                                                    </Space>
                                                    {v.summary && <Paragraph ellipsis={{ rows: 2 }} className="job-card-summary">{v.summary}</Paragraph>}
                                                    <Button type="primary" block size="large" icon={<ArrowRightOutlined />}>View & Apply</Button>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </div>
                </div>
                <AuthModal visible={authModalVisible} onClose={() => setAuthModalVisible(false)} onAuthSuccess={handleAuthSuccess} initialMode={authModalMode} />
                <CareersHelpGuide open={helpOpen} onClose={() => setHelpOpen(false)} />
            </App>
        );
    }

    // =================== JOB DETAIL + APPLICATION FORM ====================
    return (
        <App>
            <TopNav />
            <div className="careers-container" style={{ paddingTop: 24 }}>
                <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 20px' }}>
                    {/* Back button */}
                    <Button type="text" icon={<LeftOutlined />} onClick={() => setSelected(null)} style={{ marginBottom: 16 }}>
                        Back to All Jobs
                    </Button>

                    {/* Job header */}
                    <Card style={{ marginBottom: 24 }}>
                        {/* Job ad image (Oct 2026 fix): same public URL as the card; rocket placeholder if the file is missing. */}
                        {selected.jobAdImageUrl && (
                            <div style={{ margin: '-24px -24px 20px', borderRadius: '8px 8px 0 0', overflow: 'hidden' }}>
                                <JobAdImage src={selected.jobAdImageUrl} alt={selected.title} height={320} />
                            </div>
                        )}
                        <Title level={2} style={{ marginBottom: 8 }}>{selected.title}</Title>
                        <Space wrap size="middle">
                            {selected.divisionName && <Tag color="blue">{selected.divisionName}</Tag>}
                            {selected.departmentName && <Tag>{selected.departmentName}</Tag>}
                            {selected.locationName && <Text type="secondary"><EnvironmentOutlined /> {selected.locationName}</Text>}
                            {selected.employmentType && <Text type="secondary"><ClockCircleOutlined /> {selected.employmentType}</Text>}
                            {selected.workArrangement && <Tag>{selected.workArrangement}</Tag>}
                            {/* Headcount tracking (Oct 2026): always shown, singular when one position is open. */}
                            {selected.positionsAvailable > 0 && <Tag color="green"><TeamOutlined /> {selected.positionsAvailable} {selected.positionsAvailable === 1 ? 'position' : 'positions'} available</Tag>}
                            {selected.showSalary && selected.salaryFrom && (
                                <Text type="secondary"><DollarOutlined /> ${selected.salaryFrom?.toLocaleString('en-NZ')}{selected.salaryTo ? ` – $${selected.salaryTo.toLocaleString('en-NZ')}` : ''}{selected.remunerationType ? ` (${selected.remunerationType})` : ''}</Text>
                            )}
                            {selected.closeDateUtc && <Text type="secondary"><CalendarOutlined /> Applications close {new Date(selected.closeDateUtc).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' })}</Text>}
                            {selected.referenceCode && <Text type="secondary">Ref: {selected.referenceCode}</Text>}
                        </Space>
                        {/* Client UAT (Oct 2026): "View & Apply" showed only title / location / hours —
                            the summary and the full job description now render here. */}
                        {selected.summary && (
                            <Paragraph style={{ marginTop: 16, fontSize: 15 }}>{selected.summary}</Paragraph>
                        )}
                        {/* Client item C (Nov 2026): this page shows the job ADVERTISEMENT (image, summary, ad text) like SEEK.
                            The position description (PD) is internal and is no longer sent to this page. */}
                        {selected.jobAdHtml ? (
                            <div className="jd-content" style={{ marginTop: 20 }} dangerouslySetInnerHTML={{ __html: selected.jobAdHtml }} />
                        ) : (
                            <Alert type="info" showIcon style={{ marginTop: 16 }}
                                message="Full position description"
                                description="The full position description for this role is provided with your offer. If you would like it before applying, email hris@armourguard.co.nz quoting the role title." />
                        )}
                    </Card>

                    {/* Application form — wrapper div is the scroll anchor for goToStep() */}
                    <div ref={wizardRef}>
                        {!isAuthed ? (
                            /* ── APPLY GATE (client decision, 31 Aug 2026) ──────────
                               The job ad stays public; the application form does not.
                               An account is required so every application is tied to a
                               verified identity, and so the applicant can track it. */
                            <Card>
                                <Result
                                    icon={<LockOutlined style={{ color: '#1e3a5f' }} />}
                                    title="Sign in to apply"
                                    subTitle={
                                        <div style={{ textAlign: 'left', maxWidth: 520, margin: '0 auto' }}>
                                            <p style={{ marginBottom: 16 }}>
                                                You need an Armourguard careers account to apply for{' '}
                                                <strong>{selected.title}</strong>. Registering takes about a
                                                minute and lets you save a draft, track your application and
                                                apply faster next time.
                                            </p>
                                            <Alert
                                                type="info" showIcon
                                                message="Already registered?"
                                                description="Sign in with the email address you used before and this form will be pre-filled for you."
                                            />
                                        </div>
                                    }
                                    extra={[
                                        <Button key="reg" type="primary" size="large"
                                            onClick={() => openAuth("register")}
                                            style={{ background: '#1e3a5f', borderColor: '#1e3a5f', minWidth: 170 }}>
                                            Register to Apply
                                        </Button>,
                                        <Button key="login" size="large" icon={<LoginOutlined />}
                                            onClick={() => openAuth("login")}>
                                            Sign In
                                        </Button>
                                    ]}
                                />
                            </Card>
                        ) : (
                        <Card
                            title={
                                <Space>
                                    <RocketOutlined style={{ fontSize: 20, color: '#1e3a5f' }} />
                                    <span>Apply for this Position</span>
                                    {candidateInfo && <Tag color="green" icon={<CheckCircleOutlined />}>Logged in as {candidateInfo.firstName}</Tag>}
                                    {staffInfo && <Tag color="blue" icon={<CheckCircleOutlined />}>Internal applicant</Tag>}
                                </Space>
                            }
                        >
                            {hasDraft && (
                                <Alert
                                    message="Draft Resumed"
                                    description="Your previously saved draft has been loaded. Continue where you left off, or save again anytime."
                                    type="warning" showIcon icon={<EditOutlined />}
                                    style={{ marginBottom: 20 }}
                                    closable
                                />
                            )}

                            {staffInfo && (
                                <Alert
                                    message="Applying as a team member"
                                    description={`You are signed in on your Armourguard staff account (${staffInfo.email}). Draft saving is only available on a candidate account, so please complete this application in one sitting.`}
                                    type="info" showIcon style={{ marginBottom: 20 }}
                                />
                            )}

                            {/* Stepper redesign (Oct 2026): same 8 steps, icons and completed logic as the old
                                <Steps size="small"> (whose labels were cut off); clicking a step behind the
                                current one jumps back via goToStep, exactly like Previous / the Review "Edit"
                                links. Steps ahead are reached through Next (validation) only. */}
                            <StepProgress
                                ariaLabel="Application steps"
                                current={currentStep}
                                onStepClick={goToStep}
                                canGoTo={(i) => i < currentStep}
                                style={{ marginBottom: 32 }}
                                steps={[
                                    { title: 'Personal Info', icon: <UserOutlined />, complete: !!stepValidation.step1Complete },
                                    { title: 'Work Authorisation', icon: <KeyOutlined />, complete: !!formValues?.wa_nzCitizen },
                                    { title: 'Documents', icon: <FileTextOutlined />, complete: !!stepValidation.step2Complete },
                                    { title: 'Verification', icon: <SafetyCertificateOutlined />, complete: !!stepValidation.verificationComplete },
                                    { title: 'Screening', icon: <QuestionCircleOutlined />, complete: !!stepValidation.step3Complete },
                                    { title: 'Health', icon: <SafetyOutlined />, complete: !!formValues?.health_declaration },
                                    { title: 'Criminal History', icon: <IdcardOutlined />, complete: !!formValues?.crim_declaration },
                                    { title: 'Review & Submit', icon: <EyeOutlined /> }
                                ]}
                            />

                            <Form form={form} layout="vertical" onFinish={handleSubmit} onFinishFailed={handleSubmitFailed} onValuesChange={handleFormValuesChange}>
                                {/* STEP 1 */}
                                <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
                                    <Card title={<Space><UserOutlined style={{ fontSize: 20, color: '#1890ff' }} /><span>Personal Information</span></Space>} style={{ marginBottom: 24 }}>
                                        <Row gutter={16}>
                                            <Col xs={24} sm={12}><Form.Item name="firstName" label="First Name" rules={[{ required: true, message: "Required" }, vRules.personName]} normalize={vNorm.titleCase}><Input placeholder="John" size="large" prefix={<UserOutlined />} /></Form.Item></Col>
                                            <Col xs={24} sm={12}><Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: "Required" }, vRules.personName]} normalize={vNorm.titleCase}><Input placeholder="Doe" size="large" prefix={<UserOutlined />} /></Form.Item></Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col xs={24} sm={12}><Form.Item name="email" label="Email" rules={[{ required: true, message: "Required" }, { type: "email", message: "Invalid email" }]}><Input placeholder="john@example.com" size="large" prefix={<MailOutlined />} disabled={!!candidateInfo || !!staffInfo} /></Form.Item></Col>
                                            <Col xs={24} sm={12}><Form.Item name="phone" label="Mobile Number / Telephone Number" rules={[{ required: true, message: "Required" }, vRules.phoneValid]} tooltip="Include your country code — defaults to NZ +64"><PhoneField size="large" /></Form.Item></Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col xs={24} sm={12}>
                                                <Form.Item name="ageRange" label="Age Range">
                                                    <Select placeholder="Select age range" size="large" allowClear getPopupContainer={trigger => trigger.parentNode}>
                                                        {AGE_RANGE_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <Form.Item name="gender" label="Gender">
                                                    <Select placeholder="Select gender" size="large" allowClear getPopupContainer={trigger => trigger.parentNode}>
                                                        {GENDER_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col xs={24} sm={12}>
                                                <Form.Item name="ethnicity" label="Ethnicity">
                                                    <Select placeholder="Select ethnicity" size="large" allowClear showSearch
                                                        getPopupContainer={trigger => trigger.parentNode}
                                                        filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}>
                                                        {ETHNICITY_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                {(formValues.ethnicity === "Other" || formValues.ethnicity === "Other European") && (
                                                    <Form.Item name="ethnicityOther" label="Please Specify Ethnicity"
                                                        rules={[{ required: true, message: "Please provide details" }]}>
                                                        <Input placeholder="Please specify..." size="large" />
                                                    </Form.Item>
                                                )}
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col xs={24} sm={12}>
                                                <Form.Item name="employmentStatus" label="Employment Status">
                                                    <Select placeholder="Select employment status" size="large" allowClear getPopupContainer={trigger => trigger.parentNode}>
                                                        {EMPLOYMENT_STATUS_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <Form.Item label="Photo">
                                                    <Upload
                                                        beforeUpload={(file) => {
                                                            const isImg = file.type.startsWith("image/");
                                                            if (!isImg) { message.error("Image files only"); return Upload.LIST_IGNORE; }
                                                            if (file.size / 1024 / 1024 >= 3) { message.error("Max 3MB"); return Upload.LIST_IGNORE; }
                                                            setFilesByQ(prev => ({ ...prev, photo: file }));
                                                            if (candidateInfo) uploadDraftFile("photo", file);
                                                            return false;
                                                        }}
                                                        onRemove={() => { setFilesByQ(prev => { const u = { ...prev }; delete u.photo; return u; }); setDraftFiles(prev => { const u = { ...prev }; delete u.photo; return u; }); }}
                                                        maxCount={1} accept="image/*" listType="picture-card"
                                                        fileList={filesByQ.photo ? [{
                                                            uid: filesByQ.photo.uid || '-1',
                                                            name: filesByQ.photo.name || filesByQ.photo.fileName || 'photo',
                                                            status: 'done',
                                                            url: filesByQ.photo instanceof File ? URL.createObjectURL(filesByQ.photo) : filesByQ.photo.url
                                                        }] : []}
                                                    >
                                                        {!filesByQ.photo && <div><CameraOutlined style={{ fontSize: 24, color: '#1890ff' }} /><div style={{ marginTop: 8, fontSize: 12 }}>Upload Photo</div></div>}
                                                    </Upload>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Card>
                                </div>

                                {/* STEP 2 */}
                                <div style={{ display: currentStep === 2 ? 'block' : 'none' }}>
                                    <Card title={<Space><FileTextOutlined style={{ fontSize: 20, color: '#1890ff' }} /><span>Documents</span></Space>} style={{ marginBottom: 24 }}>
                                        <Title level={5}>CV/Resume <Tag color="red">Required</Tag></Title>
                                        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>Upload PDF, DOC, or DOCX (max 5MB)</Text>
                                        <Upload beforeUpload={(file) => {
                                            const valid = file.type === "application/pdf" || file.type === "application/msword" || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
                                            if (!valid) { message.error("PDF/DOC/DOCX only"); return Upload.LIST_IGNORE; }
                                            if (file.size / 1024 / 1024 >= 5) { message.error("Max 5MB"); return Upload.LIST_IGNORE; }
                                            setFilesByQ((prev) => ({ ...prev, cv: file }));
                                            if (candidateInfo) uploadDraftFile("cv", file);
                                            return false;
                                        }} onRemove={() => { setFilesByQ((prev) => { const u = { ...prev }; delete u.cv; return u; }); setDraftFiles(prev => { const u = { ...prev }; delete u.cv; return u; }); }}
                                            maxCount={1} accept=".pdf,.doc,.docx" listType="picture-card"
                                            fileList={filesByQ.cv ? [{ uid: filesByQ.cv.uid || '-1', name: filesByQ.cv.name || filesByQ.cv.fileName || 'CV', status: 'done' }] : []}>
                                            <div><UploadOutlined style={{ fontSize: 32, color: '#1890ff' }} /><div style={{ marginTop: 8 }}>Upload CV</div></div>
                                        </Upload>
                                        {filesByQ.cv && <Alert message="CV Uploaded" description={filesByQ.cv.name || filesByQ.cv.fileName || 'CV (saved)'} type="success" showIcon style={{ marginTop: 16 }} />}

                                        <Divider />
                                        <Title level={5}>Cover Letter <Tag>Optional</Tag></Title>
                                        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>Upload a file or type below</Text>
                                        <Upload beforeUpload={(file) => {
                                            const valid = file.type === "application/pdf" || file.type === "application/msword" || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
                                            if (!valid) { message.error("PDF/DOC/DOCX only"); return Upload.LIST_IGNORE; }
                                            if (file.size / 1024 / 1024 >= 5) { message.error("Max 5MB"); return Upload.LIST_IGNORE; }
                                            setFilesByQ((prev) => ({ ...prev, coverLetterFile: file }));
                                            if (candidateInfo) uploadDraftFile("coverLetterFile", file);
                                            return false;
                                        }} onRemove={() => { setFilesByQ((prev) => { const u = { ...prev }; delete u.coverLetterFile; return u; }); setDraftFiles(prev => { const u = { ...prev }; delete u.coverLetterFile; return u; }); }}
                                            maxCount={1} accept=".pdf,.doc,.docx"
                                            fileList={filesByQ.coverLetterFile ? [{ uid: filesByQ.coverLetterFile.uid || '-1', name: filesByQ.coverLetterFile.name || filesByQ.coverLetterFile.fileName || 'Cover Letter', status: 'done' }] : []}>
                                            <Button icon={<UploadOutlined />}>Upload Cover Letter</Button>
                                        </Upload>
                                        {!filesByQ.coverLetterFile && <Form.Item name="coverLetter" style={{ marginTop: 16 }}><Input.TextArea rows={6} placeholder="I am interested because..." maxLength={2000} showCount /></Form.Item>}
                                        {filesByQ.coverLetterFile && <Alert message="Cover Letter Uploaded" description={filesByQ.coverLetterFile.name || filesByQ.coverLetterFile.fileName || 'Cover letter (saved)'} type="success" showIcon style={{ marginTop: 16 }} />}

                                        <Divider />
                                        <Title level={5}><IdcardOutlined style={{ marginRight: 8 }} />Verify Identity</Title>
                                        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>Select an acceptable form of identification and upload a copy</Text>
                                        <Row gutter={16}>
                                            <Col xs={24} sm={12}>
                                                <Form.Item name="identificationType" label="Identification Type">
                                                    <Select placeholder="Select ID type" size="large" allowClear getPopupContainer={trigger => trigger.parentNode}>
                                                        {ID_TYPE_OPTIONS.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <Form.Item label="Upload ID Document">
                                                    <Upload
                                                        beforeUpload={(file) => {
                                                            const valid = file.type === "application/pdf" || file.type.startsWith("image/");
                                                            if (!valid) { message.error("PDF or image files only"); return Upload.LIST_IGNORE; }
                                                            if (file.size / 1024 / 1024 >= 5) { message.error("Max 5MB"); return Upload.LIST_IGNORE; }
                                                            setFilesByQ(prev => ({ ...prev, idDocument: file }));
                                                            if (candidateInfo) uploadDraftFile("idDocument", file);
                                                            return false;
                                                        }}
                                                        onRemove={() => { setFilesByQ(prev => { const u = { ...prev }; delete u.idDocument; return u; }); setDraftFiles(prev => { const u = { ...prev }; delete u.idDocument; return u; }); }}
                                                        maxCount={1} accept=".pdf,.jpg,.jpeg,.png"
                                                        fileList={filesByQ.idDocument ? [{ uid: filesByQ.idDocument.uid || '-1', name: filesByQ.idDocument.name || filesByQ.idDocument.fileName || 'ID Document', status: 'done' }] : []}
                                                    >
                                                        <Button icon={<UploadOutlined />}>Upload ID</Button>
                                                    </Upload>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        {filesByQ.idDocument && <Alert message="ID Document Uploaded" description={filesByQ.idDocument.name || filesByQ.idDocument.fileName || 'ID (saved)'} type="success" showIcon />}
                                    </Card>
                                </div>

                                {/* STEP 3 — VERIFICATION (re-hire). Previous-staff applications
                                are verified internally by the reporting manager of the role
                                applied for and the Head of People & Culture before entering
                                the pipeline. Previous workplace/role/period are free text. */}
                                <div style={{ display: currentStep === 3 ? 'block' : 'none' }}>
                                    <Card title={<Space><SafetyCertificateOutlined style={{ fontSize: 20, color: '#1890ff' }} /><span>Verification</span></Space>} style={{ marginBottom: 24 }}>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item
                                                    name="rehire_isPreviousStaff"
                                                    label="Have you previously worked for Armourguard?"
                                                    rules={[{ required: true, message: 'Required' }]}
                                                >
                                                    <Radio.Group
                                                        options={[{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]}
                                                        onChange={() => {
                                                            // Changing the answer clears the previous-service details
                                                            form.setFieldsValue({ rehire_previousLocation: undefined });
                                                        }}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        {formValues?.rehire_isPreviousStaff === 'Yes' && (
                                            <>
                                                <Alert
                                                    type="info" showIcon style={{ marginBottom: 20 }}
                                                    message="What happens next"
                                                    description="This step is internal only — the reporting manager for this role and the Head of People & Culture will confirm re-hire eligibility. You'll be notified when your application progresses."
                                                />
                                                <Row gutter={16}>
                                                    <Col md={12} xs={24}>
                                                        <Form.Item name="rehire_previousLocation" label="Location you worked at"
                                                            rules={[{ required: formValues?.rehire_isPreviousStaff === 'Yes', message: 'Required' }]}>
                                                            <Input placeholder="e.g. Auckland — Airport Site" size="large" maxLength={150} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col md={12} xs={24}>
                                                        <Form.Item name="rehire_previousRole" label="Role you held"
                                                            rules={[{ required: formValues?.rehire_isPreviousStaff === 'Yes', message: 'Required' }]}>
                                                            <Input placeholder="e.g. Security Officer" size="large" maxLength={150} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col md={12} xs={24}>
                                                        <Form.Item name="rehire_approxPeriod" label="Approximate period"
                                                            rules={[{ required: formValues?.rehire_isPreviousStaff === 'Yes', message: 'Required' }]}>
                                                            <Input placeholder="e.g. Mar 2021 – Nov 2023" size="large" maxLength={100} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={24}>
                                                        <Form.Item name="rehire_notes" label="Anything else we should know?"
                                                            rules={[{ required: formValues?.rehire_isPreviousStaff === 'Yes', message: 'Required' }]}>
                                                            <Input.TextArea rows={2} maxLength={500} showCount placeholder="e.g. site or team, reason for leaving" />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </>
                                        )}
                                    </Card>
                                </div>

                                {/* STEP 4 — SCREENING */}
                                <div style={{ display: currentStep === 4 ? 'block' : 'none' }}>
                                    <Card title={<Space><QuestionCircleOutlined style={{ fontSize: 20, color: '#1890ff' }} /><span>Screening Questions</span></Space>} style={{ marginBottom: 24 }}>
                                        {selected.screeningQuestions?.length > 0 ? (
                                            <>
                                                <Alert message="Additional Information Required"
                                                    description={<div>Please answer the questions below.{stepValidation.requiredTotal > 0 && <Progress percent={stepValidation.step3Progress} size="small" status={stepValidation.step3Complete ? 'success' : 'active'} format={() => `${stepValidation.requiredAnswered}/${stepValidation.requiredTotal}`} style={{ marginTop: 8 }} />}</div>}
                                                    type="info" showIcon style={{ marginBottom: 24 }}
                                                />
                                                {selected.screeningQuestions.map((q) => {
                                                    const isVisible = visibleQuestions.some(vq => vq.id === q.id);
                                                    if (!isVisible) return null;
                                                    const isConditional = !!q.parentQuestionId;
                                                    const isAnswered = isQuestionAnswered(q.id);
                                                    const parentQ = isConditional ? selected.screeningQuestions.find(p => p.id === q.parentQuestionId) : null;
                                                    return (
                                                        <Card key={q.id} id={`question-${q.id}`} size="small"
                                                            style={{ marginBottom: 16, borderLeft: isConditional ? '4px solid #faad14' : (q.isRequired && !isAnswered ? '4px solid #ff4d4f' : (isAnswered ? '4px solid #52c41a' : undefined)), backgroundColor: isConditional ? '#fffbe6' : undefined, transition: 'border-color 0.3s' }}
                                                            title={<Space><span>{q.category}</span>{isConditional && <Tag color="orange" icon={<ExclamationCircleOutlined />}>Follow-up</Tag>}</Space>}>
                                                            {isConditional && parentQ && <Alert message={`Appears because you answered "${q.parentAnswerCondition}" to: "${parentQ.questionText}"`} type="warning" showIcon style={{ marginBottom: 16, fontSize: 12 }} />}
                                                            <Form.Item name={`screening_${q.id}`} label={<Space><span>{q.questionText}</span>{renderRequiredTag(q.id, q.isRequired)}</Space>} rules={[{ required: q.isRequired, message: "Required" }]}>
                                                                {renderQuestionInput(q)}
                                                            </Form.Item>
                                                            {renderQuestionHint(q)}
                                                        </Card>
                                                    );
                                                })}
                                            </>
                                        ) : <Empty description="No screening questions" />}
                                    </Card>
                                </div>

                                {/* STEP 5 — HEALTH / TASK ABILITY QUESTIONNAIRE */}
                                <div style={{ display: currentStep === 5 ? 'block' : 'none' }}>
                                    <Card title={<Space><SafetyOutlined style={{ fontSize: 20, color: '#1890ff' }} /><span>Health &amp; Task Ability Questionnaire</span></Space>} style={{ marginBottom: 24 }}>
                                        <Alert
                                            type="info" showIcon style={{ marginBottom: 20 }}
                                            message="Questionnaire on Injury and/or Pre-existing Conditions"
                                            description="To ensure that this position would not affect the health and safety of yourself or others and your ability to carry out the tasks required, please answer the following questions. Answering yes to any question may not exclude you from employment with Armourguard Security. Your answers are kept confidential and used only for this purpose."
                                        />
                                        <div style={QROW_GROUP}>
                                            <YNRow label="1) Have you had an injury or medical condition caused by gradual process, disease or infection (e.g. occupational overuse syndrome, hearing loss, sensitivity to chemicals, shift work disorder), which the tasks of this job may aggravate or contribute to?" name="health_gradualProcess" value={formValues?.health_gradualProcess} detailLabel="If yes, please specify details" />
                                            <YNRow label="Do you have a history of occupational overuse syndrome (e.g. tennis elbow)?" name="health_ouseHistory" value={formValues?.health_ouseHistory} detailLabel="If yes, please specify details" />
                                            <YNRow label="Have you had any form of hearing loss at any time?" name="health_hearingLoss" value={formValues?.health_hearingLoss} detailLabel="If yes, please specify details" />
                                            <YNRow label="Have you had a history of migraines, headaches, or other temporary debilitating ailment?" name="health_migraines" value={formValues?.health_migraines} detailLabel="If yes, please specify details" />
                                            <YNRow label="Have you suffered pain or other problems in your wrist, forearm, elbow, shoulders, neck etc?" name="health_painProblems" value={formValues?.health_painProblems} detailLabel="If yes, please specify details" />
                                        </div>
                                        <div style={QROW_GROUP}>
                                            <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0', background: '#fafafa', fontSize: 14, lineHeight: 1.45, fontWeight: 600 }}>2) Do you have an injury or a condition that the workplace needs to accommodate so that you can complete the tasks in a safe manner?</div>
                                            <YNRow label="Do you suffer from any respiratory problems (eg. asthma)?" name="health_respiratory" value={formValues?.health_respiratory} detailLabel="If yes, please specify details" />
                                            <YNRow label="Diabetes?" name="health_diabetes" value={formValues?.health_diabetes} detailLabel="If yes, please specify details" />
                                            <YNRow label="Seizures or epilepsy?" name="health_epilepsy" value={formValues?.health_epilepsy} detailLabel="If yes, please specify details" />
                                            <YNRow label="Have you ever made a claim to ACC for any injury in the past 5 years?" name="health_accClaim" value={formValues?.health_accClaim} detailLabel="If yes, please specify details" />
                                            <YNRow label="Do you have any issues that will prevent you from standing or walking for extended periods?" name="health_standingIssues" value={formValues?.health_standingIssues} detailLabel="If yes, please specify details" />
                                            <YNRow label="Are you presently receiving medical treatment requiring you to take any form of medication?" name="health_medication" value={formValues?.health_medication} detailLabel="If yes, please specify details" />
                                            <YNRow label="Do you, or have you, had any issues with daily tasks as a result of an injury in the past (i.e. a pre existing condition)?" name="health_preExisting" value={formValues?.health_preExisting} detailLabel="If yes, please specify details" />
                                        </div>
                                        <div style={QROW_GROUP}>
                                            <div className="careers-qrow">
                                                <span className="careers-qrow-label"><span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>3) How many days off work due to illness, have you had in the past 12 months?</span>
                                                <Form.Item name="health_daysOff" rules={[{ required: true, message: 'Required' }]} className="careers-qrow-answer"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
                                            </div>
                                            <YNRow label="4) Do you have any health related issues that may impact on your ability to perform the tasks for the position you are applying for?" name="health_healthImpact" value={formValues?.health_healthImpact} detailLabel="If yes, please specify details" />
                                            <YNRow label="5) Do you consent to undergo a medical examination as part of a pre-employment process if required?" name="health_preEmploymentMedical" value={formValues?.health_preEmploymentMedical} detailOn="No" detailLabel="If no, please explain"
                                                notice={formValues?.health_preEmploymentMedical === 'No' ? MEDICAL_CONSENT_MSG : undefined} highlight={bothMedicalNo} />
                                            <YNRow label="6) If successful in your application for employment, do you consent to undergo a medical examination at any time during your employment if required by the company?" name="health_ongoingMedical" value={formValues?.health_ongoingMedical} detailOn="No" detailLabel="If no, please explain"
                                                notice={formValues?.health_ongoingMedical === 'No' ? MEDICAL_CONSENT_MSG : undefined} highlight={bothMedicalNo} />
                                            <YNRow label="7) Do you have any special needs in the workplace we should know about?" name="health_specialNeeds" value={formValues?.health_specialNeeds} detailName="health_specialNeedsDetail" detailLabel="If yes, please detail" />
                                        </div>
                                        <Divider />
                                        <Form.Item name="health_declaration" valuePropName="checked" rules={[{ validator: (_, v) => v ? Promise.resolve() : Promise.reject(new Error('Please confirm the declaration to continue')) }]}>
                                            <Checkbox>I hereby declare that the information provided in this questionnaire is to the best of my knowledge true and correct. I understand that false information given or material facts suppressed in this questionnaire may result in my dismissal and/or rejection of a claim for sickness or accident compensation (Accident Insurance Act, Section 118).</Checkbox>
                                        </Form.Item>
                                    </Card>
                                </div>

                                {/* STEP 6 — WORK AUTH & LICENCE (moved from onboarding to application).
                                Field keys (minus wa_) MIRROR the onboarding "workAuth" section
                                1:1 — the auto-pipe seeds the portal tab pre-filled at handoff. */}
                                <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
                                    <Card title={<Space><KeyOutlined style={{ fontSize: 20, color: '#1890ff' }} /><span>Work Authorisation &amp; Licences</span></Space>} style={{ marginBottom: 24 }}>
                                        <div style={QROW_GROUP}>
                                            <YNRow label="Australia/New Zealand Citizen?" name="wa_nzCitizen" value={formValues?.wa_nzCitizen} detail={false} />
                                            {formValues?.wa_nzCitizen === 'No' && <YNRow label="Permanent Resident?" name="wa_permanentResident" value={formValues?.wa_permanentResident} detail={false} />}
                                            {formValues?.wa_nzCitizen === 'No' && formValues?.wa_permanentResident === 'No' && <YNRow label="Temporary Visa with authority to work?" name="wa_visaAuthority" value={formValues?.wa_visaAuthority} options={['Yes', 'No', 'N/A']} detailLabel="If yes, visa type and expiry date" detailRows={1} detailPlaceholder="e.g. Work Visa — expires 12/2027" />}
                                        </div>
                                        {noWorkRights && (
                                            <Alert
                                                type="error" showIcon icon={<ExclamationCircleOutlined />}
                                                style={{ marginBottom: 16 }}
                                                message="You cannot proceed with this application"
                                                description="To apply for a role with Armourguard you must have the legal right to work in New Zealand — citizenship, permanent residency, or a valid work visa with authority to work. If you selected an answer in error, please correct it above to continue."
                                            />
                                        )}
                                        <div style={noWorkRights ? { opacity: 0.45, pointerEvents: 'none', userSelect: 'none' } : undefined}>
                                            <Divider orientation="left" style={{ fontWeight: 600 }}>Driver Licence</Divider>
                                            <div style={QROW_GROUP}>
                                                <YNRow label="Do you hold a valid NZ driver's licence?" name="wa_hasDriverLicence" value={formValues?.wa_hasDriverLicence} detail={false} />
                                                {formValues?.wa_hasDriverLicence === 'Yes' && (
                                                    <div style={QROW_SUBGRID}>
                                                        <Row gutter={16}>
                                                            <Col md={8} xs={24}><Form.Item label="Class" name="wa_licenceClass" rules={[{ required: true, message: 'Required' }]}><Select allowClear><Select.Option value="NZ1 (Full)">NZ1 (Full)</Select.Option><Select.Option value="NZR (Restricted)">NZR (Restricted)</Select.Option><Select.Option value="NZL (Learners)">NZL (Learners)</Select.Option></Select></Form.Item></Col>
                                                            <Col md={8} xs={12}><Form.Item label="Licence Number" name="wa_licenceNumber" rules={[{ required: true, message: 'Required' }, vRules.nzLicenceNumber]} normalize={vNorm.licenceNumberNorm}><Input placeholder="AB123456" maxLength={8} /></Form.Item></Col>
                                                            <Col md={8} xs={12}><Form.Item label="Card Version # (5b)" name="wa_cardVersion" rules={[{ required: true, message: 'Required' }, vRules.cardVersion3]} normalize={vNorm.digitsOnly(3)}><Input placeholder="3 digits" inputMode="numeric" maxLength={3} /></Form.Item></Col>
                                                        </Row>
                                                    </div>
                                                )}
                                                {formValues?.wa_hasDriverLicence === 'Yes' && <YNRow label="Demerit points or endorsements?" name="wa_demeritPoints" value={formValues?.wa_demeritPoints} detailName="wa_demeritDetails" detailLabel="If yes, details" detailRows={1} />}
                                            </div>
                                            <Divider orientation="left" style={{ fontWeight: 600 }}>Certificate of Approval (COA)</Divider>
                                            <div style={QROW_GROUP}>
                                                <YNRow label="Do you hold a valid COA?" name="wa_hasCoa" value={formValues?.wa_hasCoa} detail={false} />
                                                {formValues?.wa_hasCoa === 'Yes' && (
                                                    <div style={QROW_SUBGRID}>
                                                        <Row gutter={16}>
                                                            <Col md={12} xs={12}><Form.Item label="COA Number" name="wa_coaNumber" rules={[{ required: true, message: 'Required' }, vRules.coaNumber]} normalize={vNorm.coaNumberNorm}><Input placeholder="12-345678" maxLength={9} /></Form.Item></Col>
                                                            <Col md={12} xs={12}><Form.Item label="COA Expiry" name="wa_coaExpiry" rules={[{ required: true, message: 'Required' }, vRules.futureDate]}><Input type="date" /></Form.Item></Col>
                                                        </Row>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Card>
                                </div>

                                {/* STEP 8 — CRIMINAL HISTORY DECLARATION (candidate-experience build).
                                Field keys (minus the crim_ prefix) MIRROR the onboarding
                                "criminalHistory" section 1:1, so the auto-pipe seeds the
                                portal tab pre-filled at handoff — no re-entry, ever. */}
                                <div style={{ display: currentStep === 6 ? 'block' : 'none' }}>
                                    <Card title={<Space><IdcardOutlined style={{ fontSize: 20, color: '#1890ff' }} /><span>Criminal History Declaration</span></Space>} style={{ marginBottom: 24 }}>
                                        <Alert
                                            type="info" showIcon style={{ marginBottom: 20 }}
                                            message="Why we ask this"
                                            description="Security, Cash-in-Transit (CIT) and Logistics roles may require a Certificate of Approval (COA) and Ministry of Justice vetting. Please answer honestly — a &quot;Yes&quot; answer does not automatically exclude you from employment; it simply lets us assess your application properly. Your answers are confidential."
                                        />
                                        <div style={QROW_GROUP}>
                                            <YNRow label="Do you have a criminal record (except those in terms of the NZ Criminal Records Clean Slate Act 2004 can be withheld) that may impact on your ability to do this job, including obtaining a Certificate of Approval (COA) or similar?" name="crim_criminalRecord" value={formValues?.crim_criminalRecord} detailName="crim_criminalDetails" detailLabel="If yes, please detail" />
                                            <YNRow label="Have you ever held a security guard's licence or a Certificate of Approval licence that has been cancelled by the Registrar at any time within the last 5 years?" name="crim_coaCancelled" value={formValues?.crim_coaCancelled} detailLabel="If yes, please detail" />
                                            <YNRow label="Have you ever been convicted of a crime involving dishonesty or a specified offence at any time?" name="crim_dishonestyConviction" value={formValues?.crim_dishonestyConviction} detailLabel="If yes, please detail" />
                                            <YNRow label="Have you ever been detained in a penal institution or jail at any time?" name="crim_penalDetention" value={formValues?.crim_penalDetention} detailLabel="If yes, please detail" />
                                            <YNRow label="Are you awaiting the hearing of charges in a civil or criminal court of law?" name="crim_pendingCharges" value={formValues?.crim_pendingCharges} detailLabel="If yes, please detail" />
                                            <YNRow label="Are you currently or were you previously a member of, or otherwise involved, affiliated or associated with an NZ Gang or Motorcycle Club?" name="crim_gangAssociation" value={formValues?.crim_gangAssociation} detailLabel="If yes, please provide details" />
                                        </div>
                                        <Divider />
                                        <Form.Item name="crim_declaration" valuePropName="checked" rules={[{ validator: (_, v) => v ? Promise.resolve() : Promise.reject(new Error('Please confirm the declaration to continue')) }]}>
                                            <Checkbox>I declare that the answers above are true and complete to the best of my knowledge.</Checkbox>
                                        </Form.Item>
                                        <Form.Item name="retention_consent" valuePropName="checked" style={{ marginTop: 4 }}
                                            rules={[{ validator: (_, v) => v ? Promise.resolve() : Promise.reject(new Error('Please accept the data retention terms to submit')) }]}>
                                            <Checkbox>
                                                I consent to Armourguard retaining the information in this application for recruitment
                                                purposes. I understand that if I withdraw my application, my submitted details and
                                                documents will be deleted within 30 days.
                                            </Checkbox>
                                        </Form.Item>
                                    </Card>
                                </div>

                                {/* STEP 9 — REVIEW & SUBMIT (client request, 01 Sep 2026).
                                Read-only preview of the whole application, grouped by step.
                                Nothing here is editable — every section's Edit button jumps
                                back to its step via goToStep(). The Submit button in the NAV
                                below only renders on this step, so an application can never
                                be submitted without passing through this preview. */}
                                <div style={{ display: currentStep === 7 ? 'block' : 'none' }}>
                                    <Card title={<Space><EyeOutlined style={{ fontSize: 20, color: '#1890ff' }} /><span>Review Your Application</span></Space>} style={{ marginBottom: 24 }}>
                                        <Alert
                                            type="info" showIcon style={{ marginBottom: 20 }}
                                            message="Please check everything before you submit"
                                            description="This is exactly what our recruitment team will receive. Once submitted, your application is locked and cannot be changed — use the Edit buttons to fix anything first."
                                        />
                                        <RvwSection title="Personal Information" icon={<UserOutlined style={{ color: '#1890ff' }} />} step={0} onEdit={goToStep}>
                                            <RvwRow label="First name" value={formValues?.firstName} missing />
                                            <RvwRow label="Last name" value={formValues?.lastName} missing />
                                            <RvwRow label="Email" value={formValues?.email} missing />
                                            <RvwRow label="Mobile Number / Telephone Number" value={formValues?.phone} missing />
                                            <RvwRow label="Age range" value={formValues?.ageRange} />
                                            <RvwRow label="Gender" value={formValues?.gender} />
                                            <RvwRow label="Ethnicity" value={(formValues?.ethnicity === 'Other' || formValues?.ethnicity === 'Other European') && formValues?.ethnicityOther ? formValues.ethnicity + ' — ' + formValues.ethnicityOther : formValues?.ethnicity} />
                                            <RvwRow label="Employment status" value={formValues?.employmentStatus} />
                                            <RvwRow label="Photo" value={filesByQ.photo ? (filesByQ.photo.name || filesByQ.photo.fileName || 'Uploaded') : null} />
                                        </RvwSection>
                                        <RvwSection title="Work Authorisation & Licences" icon={<KeyOutlined style={{ color: '#1890ff' }} />} step={1} onEdit={goToStep}>
                                            <RvwRow label="Australia/New Zealand Citizen?" value={formValues?.wa_nzCitizen} missing />
                                            {formValues?.wa_nzCitizen === 'No' && <RvwRow label="Permanent Resident?" value={formValues?.wa_permanentResident} missing />}
                                            {formValues?.wa_nzCitizen === 'No' && formValues?.wa_permanentResident === 'No' && (
                                                <>
                                                    <RvwRow label="Temporary Visa with authority to work?" value={formValues?.wa_visaAuthority} missing />
                                                    {rvwVal(formValues?.wa_visaAuthorityDetails) !== null && <RvwRow label="Visa type and expiry" value={formValues?.wa_visaAuthorityDetails} />}
                                                </>
                                            )}
                                            <RvwRow label="Valid NZ driver's licence?" value={formValues?.wa_hasDriverLicence} missing />
                                            {formValues?.wa_hasDriverLicence === 'Yes' && (
                                                <>
                                                    <RvwRow label="Licence class" value={formValues?.wa_licenceClass} missing />
                                                    <RvwRow label="Licence number" value={formValues?.wa_licenceNumber} missing />
                                                    <RvwRow label="Card version # (5b)" value={formValues?.wa_cardVersion} />
                                                    <RvwRow label="Demerit points or endorsements?" value={formValues?.wa_demeritPoints} missing />
                                                    {rvwVal(formValues?.wa_demeritDetails) !== null && <RvwRow label="Demerit details" value={formValues?.wa_demeritDetails} />}
                                                </>
                                            )}
                                            <RvwRow label="Valid Certificate of Approval (COA)?" value={formValues?.wa_hasCoa} missing />
                                            {formValues?.wa_hasCoa === 'Yes' && (
                                                <>
                                                    <RvwRow label="COA number" value={formValues?.wa_coaNumber} missing />
                                                    <RvwRow label="COA expiry" value={formValues?.wa_coaExpiry} />
                                                </>
                                            )}
                                        </RvwSection>
                                        <RvwSection title="Documents" icon={<FileTextOutlined style={{ color: '#1890ff' }} />} step={2} onEdit={goToStep}>
                                            <RvwRow label="CV / Resume" value={filesByQ.cv ? (filesByQ.cv.name || filesByQ.cv.fileName || 'Uploaded') : null} missing />
                                            <RvwRow label="Cover letter" value={filesByQ.coverLetterFile ? (filesByQ.coverLetterFile.name || filesByQ.coverLetterFile.fileName || 'Uploaded file') : formValues?.coverLetter} />
                                            <RvwRow label="Identification type" value={formValues?.identificationType} />
                                            <RvwRow label="ID document" value={filesByQ.idDocument ? (filesByQ.idDocument.name || filesByQ.idDocument.fileName || 'Uploaded') : null} />
                                        </RvwSection>
                                        <RvwSection title="Verification" icon={<SafetyCertificateOutlined style={{ color: '#1890ff' }} />} step={3} onEdit={goToStep}>
                                            <RvwRow label="Have you previously worked for Armourguard?" value={formValues?.rehire_isPreviousStaff} missing />
                                            {formValues?.rehire_isPreviousStaff === 'Yes' && (
                                                <>
                                                    <RvwRow label="Location you worked at" value={formValues?.rehire_previousLocation} missing />
                                                    <RvwRow label="Role you held" value={formValues?.rehire_previousRole} missing />
                                                    <RvwRow label="Approximate period" value={formValues?.rehire_approxPeriod} missing />
                                                    <RvwRow label="Anything else we should know?" value={formValues?.rehire_notes} missing />
                                                </>
                                            )}
                                        </RvwSection>
                                        <RvwSection title="Screening Questions" icon={<QuestionCircleOutlined style={{ color: '#1890ff' }} />} step={4} onEdit={goToStep}>
                                            {visibleQuestions.length > 0
                                                ? visibleQuestions.map(q => <RvwRow key={q.id} label={q.questionText} value={screeningAnswers['screening_' + q.id]} missing={!!q.isRequired} />)
                                                : <RvwRow label="Screening questions" value="None for this vacancy" />}
                                        </RvwSection>
                                        <RvwSection title="Health & Task Ability" icon={<SafetyOutlined style={{ color: '#1890ff' }} />} step={5} onEdit={goToStep}>
                                            {HEALTH_REVIEW_ROWS.map(r => (
                                                <React.Fragment key={r.name}>
                                                    <RvwRow label={r.label} value={formValues?.[r.name]} missing />
                                                    {r.detail && rvwVal(formValues?.[r.detail]) !== null && <RvwRow label="Details" value={formValues?.[r.detail]} />}
                                                </React.Fragment>
                                            ))}
                                            <RvwRow label="Declaration — answers are true and complete" value={formValues?.health_declaration ? 'Confirmed' : null} missing />
                                        </RvwSection>
                                        <RvwSection title="Criminal History & Declarations" icon={<IdcardOutlined style={{ color: '#1890ff' }} />} step={6} onEdit={goToStep}>
                                            {CRIM_REVIEW_ROWS.map(r => (
                                                <React.Fragment key={r.name}>
                                                    <RvwRow label={r.label} value={formValues?.[r.name]} missing />
                                                    {r.detail && rvwVal(formValues?.[r.detail]) !== null && <RvwRow label="Details" value={formValues?.[r.detail]} />}
                                                </React.Fragment>
                                            ))}
                                            <RvwRow label="Declaration — answers are true and complete" value={formValues?.crim_declaration ? 'Confirmed' : null} missing />
                                            <RvwRow label="Data retention consent" value={formValues?.retention_consent ? 'Given' : null} missing />
                                        </RvwSection>
                                        {(stepValidation.canSubmit && formValues?.health_declaration && formValues?.crim_declaration && formValues?.retention_consent)
                                            ? <Alert type="success" showIcon icon={<CheckCircleOutlined />} message="Everything is complete" description="Click Submit Application below to send your application to Armourguard." />
                                            : <Alert type="warning" showIcon message="Some required information is still missing" description="Anything marked in red above needs attention — use the Edit buttons to go back and complete it, then return here to submit." />}
                                    </Card>
                                </div>

                                {/* NAV */}
                                <div style={{ marginTop: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} className="careers-wizard-nav">
                                    <Space>
                                        <Button size="large" onClick={() => goToStep(currentStep - 1)} disabled={currentStep === 0} icon={<LeftOutlined />}>Previous</Button>
                                        {candidateInfo && (
                                            <Button size="large" onClick={saveDraft} loading={draftSaving}
                                                icon={<EditOutlined />}
                                                style={{ borderColor: '#faad14', color: '#d48806' }}>
                                                Save Draft
                                            </Button>
                                        )}
                                    </Space>
                                    <Space>
                                        {currentStep < 7 ? (
                                            <Tooltip key="nav-next" title={currentStep === 1 && noWorkRights ? 'You cannot proceed — the right to work in New Zealand is required to apply.' : ''}>
                                                <Button type="primary" size="large" disabled={currentStep === 1 && noWorkRights} onClick={async () => {
                                                    if (currentStep === 1 && noWorkRights) { setWorkRightsModalOpen(true); return; }
                                                    try {
                                                        if (currentStep === 0) await form.validateFields(['firstName', 'lastName', 'email', 'phone']);
                                                        else if (currentStep === 1) await form.validateFields(WA_FIELDS);
                                                        else if (currentStep === 2 && !filesByQ.cv) { message.error('Please upload your CV'); return; }
                                                        else if (currentStep === 3) await form.validateFields(REHIRE_FIELDS);
                                                        else if (currentStep === 5) {
                                                            if (bothMedicalNo) { message.error(MEDICAL_CONSENT_MSG); return; }
                                                            await form.validateFields(HEALTH_FIELDS);
                                                        }
                                                        // Entering Review: enforce the declarations and refresh the
                                                        // preview snapshot (covers values set via setFieldsValue).
                                                        else if (currentStep === 6) { await form.validateFields(CRIM_FIELDS); setFormValues(form.getFieldsValue(true)); }
                                                        goToStep(currentStep + 1);
                                                    } catch { message.error('Please complete the required fields (marked *) to continue'); return; }
                                                }} icon={<RightOutlined />}>Next</Button>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip key="nav-submit" title={noWorkRights ? 'You cannot submit — the right to work in New Zealand is required to apply.' : (!stepValidation.canSubmit ? `Please complete all sections before submitting${!stepValidation.step1Complete ? ' — Personal Info incomplete' : ''}${!stepValidation.step2Complete ? ' — CV required' : ''}${!stepValidation.verificationComplete ? ' — Verification step incomplete' : ''}${!stepValidation.step3Complete ? ` — ${stepValidation.requiredTotal - stepValidation.requiredAnswered} required question(s) remaining` : ''}${!stepValidation.medicalConsentOk ? ' — Health questionnaire: at least one of questions 5 and 6 must be Yes' : ''}` : 'All sections complete — ready to submit!')}>
                                                <Button
                                                    type={stepValidation.canSubmit ? "primary" : "default"}
                                                    size="large" htmlType="button" onClick={() => { if (!applying && !submitLock.current) form.submit(); }} loading={applying} disabled={!submitArmed || noWorkRights || !stepValidation.canSubmit || !formValues?.health_declaration || !formValues?.crim_declaration || !formValues?.retention_consent}
                                                    icon={<CheckCircleOutlined />}
                                                    style={{
                                                        minWidth: 160,
                                                        ...(stepValidation.canSubmit
                                                            ? { backgroundColor: '#1e67e6', borderColor: '#1e67e6', color: '#fff' }
                                                            : { backgroundColor: '#d9d9d9', borderColor: '#d9d9d9', color: '#8c8c8c', cursor: 'not-allowed' })
                                                    }}
                                                >Submit Application</Button>
                                            </Tooltip>
                                        )}
                                    </Space>
                                </div>
                                {currentStep === 3 && stepValidation.requiredTotal > 0 && !stepValidation.step3Complete && <Alert message={`${stepValidation.requiredTotal - stepValidation.requiredAnswered} required ${stepValidation.requiredTotal - stepValidation.requiredAnswered === 1 ? 'question' : 'questions'} remaining`} type="warning" showIcon style={{ marginTop: 16 }} />}
                            </Form>
                        </Card>
                        )}
                    </div>
                </div>
            </div>
            <AuthModal visible={authModalVisible} onClose={() => setAuthModalVisible(false)} onAuthSuccess={handleAuthSuccess} initialMode={authModalMode} />
            <CareersHelpGuide open={helpOpen} onClose={() => setHelpOpen(false)} />

            {/* ── Work-rights gate modal: no citizenship / residency / work visa ── */}
            <Modal
                open={workRightsModalOpen}
                onCancel={() => setWorkRightsModalOpen(false)}
                footer={<Button type="primary" onClick={() => setWorkRightsModalOpen(false)}>I understand</Button>}
                title={<span><ExclamationCircleOutlined style={{ color: '#cf1322', marginRight: 8 }} />You cannot proceed with this application</span>}
                centered destroyOnClose zIndex={12000}>
                <p style={{ marginBottom: 12 }}>
                    To apply for a role with Armourguard you must have the <strong>legal right to work in New Zealand</strong> —
                    New Zealand or Australian citizenship, permanent residency, or a valid work visa with authority to work.
                </p>
                <p style={{ marginBottom: 12 }}>
                    Based on your answers, you do not currently hold work rights, so unfortunately your application
                    cannot proceed further.
                </p>
                <p style={{ marginBottom: 0, color: '#64748b' }}>
                    If you selected an answer in error, close this message and correct your answers to continue.
                    If you obtain a work visa in the future, we would be delighted to hear from you again.
                </p>
            </Modal>
        </App>
    );
}

const root = createRoot(document.getElementById("careers-root"));
root.render(<CareersApp />);
